import { Api, ApiError, OFFLINE, type PublicKeyCredentialCreationOptionsJSON } from "../api/client.js";
import type { AnswerBody, AssertionOptions, BundleView, CardView, Channel, Clarification, DraftOutcome, Eligibility, FreezeField, KycConfirmed, MandateView, NextAction, Receipt, RefusedStep, SupportContact } from "../api/types.js";
import { FixtureDrafter, type DraftFixture } from "../adapters/drafter.js";
import { ReadQuestions, type ReadAsk } from "../adapters/reads.js";
import fixtureData from "../adapters/fixtures.generated.json" with { type: "json" };
import { LevelMeter, SpeechPulse, micShareable } from "../adapters/level.js";
import { VoiceInput, webSpeechFactory, type RecognizerFactory } from "../adapters/voice.js";
import type { AudioOut } from "../adapters/audio-out.js";
import type { SpeechSource } from "../adapters/speech.js";
import type { AmendSource } from "../adapters/amend.js";
import { CardEditor, EDIT_WORDS, refusalText, type EditLeft, type Removal } from "./editor.js";
import { ReadAloud, outcomeParts, type ReadAloudMode } from "./read-aloud.js";
import { EndOfSpeech, settle, type Ending, type EosMode } from "./end-of-speech.js";
import { reducedMotion } from "../lib/root.js";
import { BankCard } from "../ui/bankcard.js";
import { ChatView, type ChipOption, type OfferTarget, type OpenOffer, type QuestionHandle } from "../ui/chat.js";
import { countUp, openSheet, passkeySheet, scheduledSheet, successSheet, supportSheet, type SheetHandle } from "../ui/sheet.js";
import { scheduledSuccess } from "./schedule-success.js";
import { accountChips, answerable, answerableAll, answerTarget, choiceChip, declines, differentAmount, interpret, isAccountQuestion, LET_GO, newRequest, offerReply, reaskLine, sameQuestion, START_OVER, takesWords, type ReadChip, type Reply } from "./clarify.js";
import type { Persona } from "./personas.js";
import { isCancellation, isUnsupported, registrationOptions, signNow, webAuthnAvailable } from "./signing.js";
import { createThenSign, markPasskeyHere, passkeyHere, whenFocused } from "./passkey.js";
import { HeldFlow } from "./held.js";
import { StepUpFlow } from "./stepup.js";
import { errorCode, FREEZE_CODES, freezeMessage, refusalMessage, type Message } from "./stop.js";
import { asFreeze, asRecheck, clearedLine, freezeRow, passGroups, recheckRow } from "./freeze-judge.js";
import { DEMO_FAULT_LINES, demoLabelOf, demoResultLine } from "./demo-fault.js";
import { reaskChannel } from "./kyc-model.js";
import { VOICE_CONSENT } from "./voice-consent.js";
import type { JRow } from "./judge-model.js";
import { clearedJudgeView, freezeJudgeView } from "../ui/judge.js";
import { SAY } from "./bank-words.js";
import { answerRefused, assistantSide, failureOf, keptLine, nameDivergence, nearMiss, offerKept, openSurface, pausedByBank, pointerOf, questionGone, reasksAfterBlock, unresolvedPayee, wayOn, withFlags, withNote, withSkipped, type SurfaceOpeners } from "./surfaces.js";
import { afterLostConfirm, afterReload, balanceChanged } from "./card-end.js";
import { Drafting, NoFixtureDraft, type Turn } from "./drafting.js";
import { wait } from "./waiting.js";
import { LiveCards } from "./live-cards.js";
import { bankPausedNotice, drawsClause, pausedNotice, type ScamQuestionId } from "./scam.js";
import { collapseRefusals, eligibilityLine, outcomePlan } from "./outcome-plan.js";
import { successModel, type Snapshot } from "./success-model.js";
import { balanceAnswer, billAnswer, billsAnswer, cardAnswer, cardAnswerFor, limitsAnswer, pendingAnswer, recentAnswer, spendAnswer, type CardAnswer, type ReadAnswer } from "./reads.js";
import { cardBill, cardChoices, myCard, NO_CARD_LINE, WHICH_CARD, type CardChoice } from "./card-choice.js";
import { bankReadsOf } from "./bank-reads.js";
import { explainedOf } from "./explained.js";
import { CardDetails, readCard, scheduledReadCard } from "../ui/readcard.js";
import { scheduleRows } from "./mandates-model.js";
import { clock } from "../lib/format.js";
import { rebuildStream, snapStream } from "../lib/stream-snapshot.js";
import type { ChatKept, KeptWords } from "./resume.js";

/**
 * The chat controller (UX spec v3 §6.2, §6.8, §6.3, §6.12): what the customer
 * says, what the gateway answers, which card that becomes, and the signature.
 *
 * Rules this file is built around:
 *  - a bank card is drawn from `GET /v1/bundles/{id}` and nothing else;
 *  - one live card at a time — an edit or a new request supersedes the old one
 *    (it stays in the stream, collapsed and marked), and the old bundle is
 *    cancelled on the server so it can never be signed;
 *  - the sign tap's first asynchronous act is `navigator.credentials.get()`
 *    (options are fetched when the card is drawn); the enrolment tap's is
 *    `navigator.credentials.create()` (options fetched when the sheet opens);
 *  - nothing on a living card ticks; at `expires_at` it turns Expired and one
 *    line asks again with the same words;
 *  - voice carries no authority: it fills the stream, it never signs;
 *  - DCTA-141: the app decides when the customer has finished speaking (`end-of-speech.ts`),
 *    never the engine's short silence — tap = until Done or adaptive silence, hold = until
 *    the release, and for Mdm Lim (large type) silence only stops listening: the words wait
 *    in the field with Send;
 *  - DCTA-181: Done never discards speech — the act ends LISTENING; the engine finishes (what
 *    it holds goes to Tencent, its last words arrive) and the words are settled on its last
 *    result (`settle`); words it never confirmed wait in the field; a miss is said once.
 */
export interface ChatDeps {
  readonly api: Api;
  readonly persona: Persona;
  readonly name: string;
  /** The column the sheets rise in (`#app`). */
  readonly host: HTMLElement;
  readonly root: HTMLElement;
  readonly onBack: () => void;
  readonly onSignedOut: () => void;
  /**
   * DCTA-231 V2: the bank ran a tightening without a signature (a lock, a lower limit, a frozen card, a stopped schedule) —
   * the session goes on, and the screens that show it (Home, Invest) read again at once, never a moment of the old state.
   */
  readonly onBankChanged?: () => void;
  /** The success sheet's Receipt pill (P2-1): this bundle in Activity. */
  readonly onReceipt?: ((bundleId: string) => void) | undefined;
  /**
   * DCTA-131 (iPhone: "S$613.40 owed" beside the bank's "Nothing is owed"): a signed bundle ran at the bank — its
   * receipt is in. Home and an account's page under the chat are read again, so nothing behind the chat keeps the
   * balances from before the signature.
   */
  readonly onMoneyMoved?: (() => void) | undefined;
  readonly recognizer?: RecognizerFactory | undefined;
  /** May a level meter open the microphone beside the speech engine? Default: not on iOS (DCTA-89, adapters/level.ts). */
  readonly shareMic?: boolean | undefined;
  /** Judge mode is on: a transport code (OFFLINE) is shown to the jury, never to a customer (DCTA-85 P2-2). */
  readonly judgeOn?: (() => boolean) | undefined;
  /** The bank's clock (DCTA-85 P1-2, lib/bank-clock.ts): every time printed here is the bank's; `undefined` = none yet. */
  readonly bankNow?: (() => Date | undefined) | undefined;
  /**
   * DCTA-120 (spec §F.2 row 5): 17 Payees at `add`, pushed over the chat — the assistant's pointer for "add a payee".
   * Absent → the pointer is not drawn (never a chip that does nothing).
   */
  readonly onAddPayee?: (() => void) | undefined;
  /**
   * DCTA-154 (backend DCTA-153 ruling 3): the Limits page, over the chat — where a payment's cap refusal points instead
   * of naming a figure ("See my limits"). Absent → that chip is not drawn.
   */
  readonly onLimits?: (() => void) | undefined;
  /** DCTA-204 (R7): the bank's "See my activity" opens the Activity tab. */
  readonly onActivity?: (() => void) | undefined;
  /**
   * DCTA-209 (F56, F61): a schedule, pushed over the chat — a signed schedule card's "View", a read card's row. Absent → those
   * rows and the View action are not drawn as ways on.
   */
  readonly onSchedule?: ((mandateId: string) => void) | undefined;
  /**
   * DCTA-162 (spoken.md F30, F31): the bank's voice — where its sentences come from (the speech route, or the stand-in),
   * the speaker, and the customer's Read aloud choice. Absent → nothing is ever read aloud.
   */
  readonly readAloud?: { readonly source: SpeechSource; readonly out: AudioOut | undefined; readonly mode: () => ReadAloudMode } | undefined;
  /**
   * DCTA-170 (docs/design/v3/03-edit-in-place.md): where a card's edits are made — the bank's amend route, or the stand-in.
   * Absent → Edit sends the customer to the conversation, as before (B46 is not built yet).
   */
  readonly amend?: AmendSource | undefined;
  /**
   * DCTA-176 (F45): the identity check's way on — the refusal's "Update my details" (`support`, topic `kyc_refresh`) opens
   * the refresh over the chat. Resolves false when this bank has no refresh (not demo mode): Talk to us instead, as before.
   */
  readonly onKycRefresh?: (() => Promise<boolean>) | undefined;
  /**
   * DCTA-188 Change 4: judge mode's one-shot faulty-drafter switch (app/demo-fault.ts) — the next request goes to the demo
   * route while it is on. The same switch judge mode's home draws; absent outside a session that has it.
   */
  readonly demoFault?: { readonly armed: boolean; take(): boolean } | undefined;
  /**
   * DCTA-191 (docs/specs/fe-audio-evidence-dcta191.md, ADR-0024): the listening's PCM the Tencent engine keeps
   * (adapters/pcm-clip.ts). Cleared when a listening starts, on Cancel and when Tencent steps aside; taken by the next
   * words sent — a spoken request's goes to the bank as its evidence, any other is dropped.
   */
  readonly audio?: { clear(): void; take(): Uint8Array | undefined } | undefined;
}

interface Live {
  /** DCTA-170: an edit in place draws each new version of the card in this one's place — both change with it. */
  card: BankCard;
  bundle: BundleView;
  /** The words this card came from and how they came: Ask again, Try again and Update send them again. */
  readonly turn: Turn;
  options: AssertionOptions | undefined;
  /** The bank has no passkey for this customer: the Sign tap creates one first (§C.11). */
  reg: PublicKeyCredentialCreationOptionsJSON | undefined;
  before: Promise<Snapshot | undefined>;
  expiry: ReturnType<typeof setTimeout> | undefined;
  stepUp: StepUpFlow | undefined;
  /** DCTA-170 (22s): the steps removed by edits, listed with Undo above Sign until the card is signed — through a later edit. */
  removed: readonly Removal[];
}

/**
 * A question the customer can answer now (04), and where its answer goes. DCTA-189: the bank may ask two in one turn (one
 * card); after one is answered it asks the other again under a new draft — the same question keeps its place on the card
 * and its chips, and only its draft and id change here (`#rebind`), so its chips answer the bank's new draft.
 */
interface OpenQuestion {
  draftId: string;
  question: Clarification;
  /**
   * The sentence that raised it, how it came, and the draft it became on the fixture path —
   * "Ask again" and "Start again" re-send these words; a freeze after an answer puts the
   * draft's readings side by side.
   */
  readonly turn: Turn;
  readonly handle: QuestionHandle;
  /** DCTA-136 (F19): the chips the app drew from the bank's own read (an account question with none), kept to ask again. */
  readonly read: readonly ReadChip[];
  /**
   * Asked once more already — after words that named no option (here), or after the bank refused an answer and kept the
   * question (DCTA-136): a second miss lets the question go, so no loop of re-asks or refusals is possible.
   */
  readonly reasked: boolean;
}

/** DCTA-176 (F49): a turn the identity check refused — the words as said, the bank's answer, the bank's time of it. */
export interface EligibleTurn {
  readonly said: string;
  readonly outcome: DraftOutcome;
  readonly at: string | undefined;
}

/** Claude's ruling 3 (13 Oct, P3-B4): the way from the bank's kept-card line to that card. */
const SHOW_KEPT = "Show the earlier card";
/** DCTA-203: a read the bank answered that this app has no card for (copy for ux-designer). */
const BANK_ANSWERED = "The bank answered that, but this screen can't show its answer yet.";
/** DCTA-203: card details for every card the bank's read lists (copy for ux-designer). */
const SEVERAL_CARDS = "Here are your cards. Tap Show on the one you need.";
const NOT_YET = "I can't do that one yet. Say it another way, or type it.";
/** §C.11 / §C.13: the lines above the Sign button of a first payment. */
const FIRST_PAYMENT = "First payment here: your phone saves a passkey, then signs with it.";
const SAVED_NOW = "Passkey saved. Now confirm the payment.";
const SAVED = "Passkey saved.";
const DIFFERENT_AMOUNT = "A different amount";
/** DCTA-136 (review): the accounts read that draws an account question's chips waits this long, then the question goes without them. */
const READ_CHIPS_MS = 3000;

/** DCTA-111: the customer's Cancel during the wait, and the app's own give-up (§6.2 drew neither; flagged for ux-designer). */
const CANCELLED = "Cancelled. Nothing was sent.";
/**
 * DCTA-162 (Claude's brief: "say so quietly"): the browser's engine took over a listening because Tencent's was not here.
 * Drafted by the build with design:ux-copy — no spec row yet; flagged for ux-designer in the journal.
 */
const ENGINE_ASIDE = "Using this device's speech recognition for now.";
/** §F.2 19j — accepted as built. */
const CANT_HEAR = "I couldn't hear that. Say it again, or type it.";
/** No speech route given: the bank says nothing. */
const SILENT: SpeechSource = { plan: () => Promise.resolve(undefined), audio: () => Promise.resolve(undefined) };
const TOO_LONG = "That took too long, so I stopped. Nothing was sent — try again in a moment.";
/**
 * QA-04 (the coordinator's words, 14 Oct; for ux-designer): the page reloaded while the bank was answering — the answer
 * never arrived here. It claims nothing about the bank's side; "Ask again" under it holds the customer's last words.
 */
const INTERRUPTED = "The answer was interrupted.";
/** DCTA-221 review: how long after a restore the composer's polite region says the interruption (the chat is mounted by then). */
const ANNOUNCE_MS = 250;
/** DCTA-221 review: a card's read-back after a reload that found no answer, or the bank mid-signature, is asked once more. */
const READ_AGAIN_MS = 1500;
const NUMBER = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

/** How one round trip is drawn: an answer to a question (which one, what was sent, and how it was given), or a scoped offer's card. */
interface RunHow {
  readonly answering?: boolean;
  readonly question?: OpenQuestion;
  readonly answered?: AnswerBody;
  readonly scoped?: boolean;
  /** DCTA-221: the answer was the customer's own words, typed or said (never a chip's tap) — the reply is always drawn. */
  readonly byWords?: boolean;
  /**
   * DCTA-226 (§5.7): words that ask, sent to an open offer. `declined`: the bank does not answer them there (400, nothing
   * claimed) — nothing is drawn for it; the caller does what the words did before.
   */
  readonly atOffer?: { readonly declined: () => void };
}

export class Chat {
  readonly view: ChatView;
  readonly #d: ChatDeps;
  /** Where the words go to be drafted — the bank's models, or the reviewed fixtures (DCTA-111). */
  readonly #drafts: Drafting;
  readonly #reads = new ReadQuestions();
  /** Card details on screen: hidden again when the customer leaves the chat or the page. */
  readonly #details = new Set<CardDetails>();
  readonly #onHidden = (): void => {
    if (document.visibilityState !== "hidden") return;
    this.#hideDetails();
    // DCTA-162: the page went away — the bank stops reading.
    this.#speech.stop();
    // DCTA-181: and stops listening — nothing said while the customer is away goes anywhere. Words already heard wait
    // for review when they come back; with none, the listening simply ends.
    const eos = this.#eos;
    if (eos === undefined || eos.ended !== undefined || !this.#listening()) return;
    if (eos.transcript === "") this.#cancelListening();
    else this.#finish(eos.lost(performance.now()));
  };
  /** DCTA-162 (F30, F31): what the bank reads aloud and when; the composer's Stop pill follows it. */
  readonly #speech: ReadAloud;
  readonly #voice: VoiceInput;
  readonly #level = new LevelMeter();
  /** iOS: the bars follow the engine's words — the engine is the microphone's only user (DCTA-89). */
  readonly #pulse = new SpeechPulse();
  readonly #shareMic: boolean;
  /**
   * The cards still in play (DCTA-124): the newest, and — after a scoped offer was accepted — the card
   * it was offered beside (`live-cards.ts`). Every card's buttons act on its OWN entry.
   */
  readonly #cards = new LiveCards<Live>();
  #editing: Live | undefined;
  /** DCTA-170: the card being edited in place, and its editor (one at a time); an Undo after Done in flight. */
  #editor: { readonly live: Live; readonly editor: CardEditor } | undefined;
  #undoing = false;
  #busy = false;
  /** One request at a time. While a turn is in flight the composer holds Send (§D.9): on screen, not acting. */
  get #drafting(): boolean {
    return this.#busy;
  }
  set #drafting(on: boolean) {
    this.#busy = on;
    this.view.composer.hold(on);
  }
  #signing = false;
  #sheet: SheetHandle | undefined;
  /** Does the bank know a passkey for this customer (its credential list, read once a session)? */
  #known: Promise<boolean> | undefined;
  /** DCTA-141: this listening's end-of-speech — when it is over and what happens to the words. */
  #eos: EndOfSpeech | undefined;
  /** The one timer that wakes the end-of-speech module at its next deadline. */
  #eosTimer: ReturnType<typeof setTimeout> | undefined;
  /** An ending decided, waiting for the engine to finish (a send keeps the engine's last words). */
  #closing: Ending | undefined;
  #voiceCancelled = false;
  /** DCTA-162: the browser's engine took over this listening — said once, quietly, when the listening ends. */
  #engineNote = false;
  /** DCTA-181 (DCTA-178): the last "I couldn't hear that" — while it is still the newest line, a second miss adds nothing. */
  #cantHearLine: HTMLElement | undefined;
  /** DCTA-181: this listening's start, whether its engine holds the microphone, and the first chunk (`data-voice-mic`). */
  #listenAt = 0;
  #engineMic = false;
  /**
   * Claude's ruling 2 (13 Oct): the voice-clip line under the composer — unseen until the first listening whose engine
   * keeps a clip (Tencent's, holding the microphone); it stays through that turn's words and answer and goes when the
   * next turn begins; never again this session.
   */
  #consent: "unseen" | "showing" | "sent" | "gone" = "unseen";
  #firstChunkAt: number | undefined;
  /** The questions the customer can answer now, in the bank's order: one, or (DCTA-189) the ones the bank asked together. */
  #qs: readonly OpenQuestion[] = [];
  /** DCTA-208: the app's own "Which card?" (its words reach no bank), open until a chip or the next request answers it. */
  #cardPick: QuestionHandle | undefined;
  /** The first open question — the one the customer's words answer unless they name another's chip or figure. */
  get #question(): OpenQuestion | undefined {
    return this.#qs[0];
  }
  /** DCTA-139 (F13): while an open question takes words or a figure, the field asks for the answer (described by them). */
  #setOpen(open: readonly OpenQuestion[]): void {
    this.#qs = open;
    const ids = open.filter((o) => takesWords(o.question)).map((o) => o.handle.describedBy);
    this.view.composer.answering(ids.length === 0 ? undefined : ids.join(" "));
  }
  /**
   * DCTA-189: the card's other questions while the bank answers one of them — their ids are the draft that answer ends.
   * They stay only if the bank asks them again, the same (`#rebind`); any other end of the answer ends them (`#endWaiting`).
   */
  #waiting: readonly OpenQuestion[] = [];
  /**
   * DCTA-189: a chip tapped on a waiting question — held, and sent by `#run` (inside the same wait) the moment the bank asks
   * that question again, the same (`#rebind` opens it again; DCTA-221: or draws it again, and the tap answers it there);
   * otherwise released with the rest (`#endWaiting`).
   */
  #queued: { readonly open: OpenQuestion; readonly reply: Exclude<Reply, { kind: "unmatched" }>; readonly index: number } | undefined;
  /** DCTA-189: this answer only gave the card's questions their new ids — nothing new on the screen, nothing read aloud. */
  #rebound = false;
  /** Every step-up still waiting — a newer card does not end an older signed one's window — and the card it waits on. */
  readonly #flows = new Map<StepUpFlow, Live>();
  /**
   * QA-04: the words of the turn the bank is answering now (a request, an answer — its question's own words — or a read),
   * kept for the page's next life: a reload in the middle says the answer was interrupted and offers them again.
   */
  #inFlight: KeptWords | undefined;
  /** QA-04: each new turn moves it on — a card a reload is still reading back from the bank stays as drawn once one has. */
  #epoch = 0;
  /** QA-04: the cards a reload is still reading back from the bank — kept as in play if the page goes again meanwhile. */
  readonly #readingBack = new Map<string, KeptWords & { readonly id: string }>();
  /** DCTA-221 review: a read-back found no answer twice — "Can't reach the bank right now." is said once a restore. */
  #unreachedSaid = false;
  /** The bank's stage timings of the last draft and the last confirm (ask g), for judge mode. */
  #timings: { draft?: Readonly<Record<string, number>>; confirm?: Readonly<Record<string, number>> } = {};
  /** DCTA-176 (F49): the last turn the identity check refused before resolution — judge mode's Proof reads it. */
  #eligible: EligibleTurn | undefined;

  constructor(deps: ChatDeps) {
    this.#d = deps;
    this.#drafts = new Drafting(deps.api, new FixtureDrafter(fixtureData as readonly DraftFixture[]), deps.demoFault);
    this.view = new ChatView({
      onBack: () => this.#back(),
      // DCTA-141 rule 6: words from the microphone keep `voice` through a review, edited or not. DCTA-162: a turn the
      // customer said or typed decides whether the bank's answer is read aloud (spoken.md §1.1) — a chip is not a turn.
      onSend: (text, channel) => {
        this.#speech.turn(channel);
        void this.say(text, channel);
      },
      onMic: () => this.#toggleMic(),
      onCancel: () => this.#cancelListening(),
      onHold: () => this.#startListening("hold"),
      onRelease: () => this.#act((eos, at) => eos.release(at)),
      onHoldLost: () => this.#holdLost(),
      onDone: () => this.#act((eos, at) => eos.done(at)),
      onEdit: () => this.#act((eos, at) => eos.edit(at)),
      // F31: Stop, or a touch of the field, while the bank reads aloud — silence at once.
      onStopSpeech: () => this.#speech.stop(),
      onFieldTouch: () => this.#speech.stop(),
    });
    const voiceOut = deps.readAloud;
    this.#speech = new ReadAloud({
      source: voiceOut?.source ?? SILENT,
      out: voiceOut?.out,
      mode: voiceOut?.mode ?? (() => "never"),
      onSpeaking: (part) => this.view.composer.speaking(part),
    });
    // iOS plays nothing a page starts by itself: in Always any tap or key in the chat unlocks the speaker (a typed turn is
    // read aloud there); the mic's tap does in When I speak too (below).
    for (const part of [this.view.body, this.view.dock]) {
      part.addEventListener("click", () => this.#speech.unlock("tap"), { capture: true });
      part.addEventListener("keydown", () => this.#speech.unlock("tap"), { capture: true });
    }
    this.view.greet(deps.name);
    this.#shareMic = deps.shareMic ?? micShareable(navigator);
    this.#voice = new VoiceInput(deps.recognizer === undefined ? webSpeechFactory() : deps.recognizer);
    this.#voice.onTranscript((text, final) => this.#heardWords(text, final));
    // DCTA-181: the bars follow the microphone itself, from its first chunk (Tencent's engine holds it).
    this.#voice.onLevel((level) => {
      this.#firstChunkAt ??= performance.now();
      this.#pulse.level(level);
    });
    this.#voice.onSegment(() => this.#eos?.segment(performance.now()));
    this.#voice.onState((state) => {
      if (state === "idle") this.#listeningEnded();
      if (state === "unavailable") this.#micOff();
    });
    this.#voice.onError((code) => {
      if (code === "not-allowed" || code === "service-not-allowed") this.#micOff();
    });
    if (!this.#voice.available) this.view.composer.disable();
    document.addEventListener("visibilitychange", this.#onHidden);
  }

  /** DCTA-162: Tencent's engine stepped aside for the browser's (once a page): the chat says so when this listening ends. */
  engineAside(): void {
    this.#engineNote = true;
    // DCTA-191: the browser's engine records nothing — what Tencent kept before it stepped aside is not the turn's audio.
    this.#d.audio?.clear();
  }

  /** The microphone can be used (an engine, and no refusal yet) — the mark asks before it charges. */
  get voiceAvailable(): boolean {
    return this.#voice.available;
  }

  /** A signature is in flight: Back must not take the chat away (DCTA-84 A1). */
  get signing(): boolean {
    return this.#signing;
  }

  /** DCTA-176 (F49): the last turn the identity check refused in this chat, for judge mode's Proof. */
  get eligibilityRefusal(): EligibleTurn | undefined {
    return this.#eligible;
  }

  /**
   * QA-04: what this chat shows, for the page's next life (app/resume.ts) — the past as data (card details masked first),
   * the turn in flight, the cards still in play by id (the bank is asked about them again, never trusted from here), the
   * open questions with their draft, the path the bank drafts on, whether the voice-clip line was shown, unsent words.
   */
  kept(customerId: string): ChatKept {
    this.#hideDetails();
    const pending = this.#inFlight;
    const open = pending === undefined ? this.#qs : [];
    const first = open[0];
    // An open question's turn and what follows it are drawn again, live — not kept as the past.
    const asked = first === undefined ? undefined : (this.view.body.querySelector(`[id="${first.handle.describedBy}"]`) ?? undefined);
    const live: (KeptWords & { id: string })[] = [];
    for (const l of [...this.#cards.all(), ...this.#flows.values()]) {
      const inPlay = l.card.life === "live" || l.card.life === "editing" || l.stepUp?.open === true;
      if (inPlay && !live.some((k) => k.id === l.bundle.bundle_id)) live.push({ id: l.bundle.bundle_id, said: l.turn.said, channel: l.turn.channel });
    }
    for (const l of this.#readingBack.values()) if (!live.some((k) => k.id === l.id)) live.push(l);
    const path = this.#drafts.path;
    const field = this.view.composer.value;
    return {
      customerId,
      nodes: snapStream(this.view.body, { before: asked }),
      ...(pending === undefined ? {} : { pending }),
      ...(live.length === 0 ? {} : { live }),
      ...(first === undefined ? {} : { open: { draftId: first.draftId, questions: open.map((o) => o.question), said: first.turn.said, channel: first.turn.channel, read: first.read } }),
      ...(path === "unknown" ? {} : { path }),
      ...(this.#consent === "unseen" ? {} : { consent: true }),
      ...(field.trim() === "" ? {} : { field }),
    };
  }

  /**
   * QA-04: the conversation a reload kept, drawn back before the chat is in the page — the past, every control in it spent;
   * then what is live again, from the bank, never from what was kept: an answer that was in flight says it was interrupted
   * and offers the customer's words again (never sent by itself); open questions are asked again, live, on their draft;
   * each card that was in play is read again and drawn in its place (`#again`).
   */
  restore(kept: ChatKept): void {
    if (kept.path !== undefined) this.#drafts.restore(kept.path);
    if (kept.consent === true) this.#consent = "gone";
    this.view.restore(rebuildStream(kept.nodes));
    if (kept.field !== undefined) this.view.composer.setValue(kept.field);
    const pending = kept.pending;
    if (pending !== undefined) {
      this.view.assistant(INTERRUPTED);
      // DCTA-221: a new turn in the way the words first came — never the identity check's `reask` (DCTA-176 B61), which the
      // bank records as a re-ask of words a KYC refusal kept (none here: `reask_unrecorded`, voice assumed).
      this.view.actions([{ label: "Ask again", small: `“${pending.said}”`, name: `Ask again: ${pending.said}`, once: true, onSelect: () => void this.say(pending.said, pending.channel) }]);
      // DCTA-221 review (WCAG 4.1.3): drawn before the chat is in the page, the log never says it — the composer's polite
      // region does, once the chat is on the page (it is mounted right after this).
      setTimeout(() => {
        if (this.view.dock.isConnected) this.view.composer.status(INTERRUPTED);
      }, ANNOUNCE_MS);
    } else if (kept.open !== undefined) {
      const o = kept.open;
      const path = this.#drafts.path === "unknown" ? undefined : this.#drafts.path;
      const turn: Turn = { said: o.said, channel: o.channel, draft: path === "fixture" ? this.#drafts.draftOf(o.said) : undefined, path };
      this.#ask({ draft_id: o.draftId, kind: "clarification", policy_version: "", audit_ref: "", clarifications: o.questions }, document.createElement("div"), turn, { read: o.read });
    }
    const epoch = this.#epoch;
    for (const l of kept.live ?? []) {
      this.#readingBack.set(l.id, l);
      void this.#again(l, epoch).finally(() => this.#readingBack.delete(l.id));
    }
    this.view.scrollToNewest();
  }

  /**
   * QA-04: a card that was in play when the page reloaded, read again from the bank and drawn in its kept copy's place —
   * live (Sign, its own expiry on the bank's clock) only while the bank still waits for its signature in time; otherwise its
   * end, as the bank's record says (a signature that arrived: the receipt, and the code field when the bank waits for one).
   * In flight or unknown, or once the customer has moved on: the copy stays as it was drawn.
   */
  async #again(l: KeptWords & { readonly id: string }, epoch: number): Promise<void> {
    const read = (): Promise<BundleView | undefined> => this.#d.api.bundle(l.id).catch(() => undefined);
    let bundle = await read();
    // DCTA-221 review: no answer, or the bank mid-signature — once more, a moment later (a signature the reload interrupted
    // is usually settled by then); still no answer: say so (#lostConfirm's line), never a card left "to sign" in silence.
    if (bundle === undefined || bundle.state === "signing") {
      await new Promise((r) => setTimeout(r, READ_AGAIN_MS));
      if (epoch !== this.#epoch) return;
      bundle = await read();
    }
    if (bundle === undefined) {
      if (epoch === this.#epoch && !this.#unreachedSaid) {
        this.#unreachedSaid = true;
        this.view.message(["Can't reach the bank right now."]);
      }
      return;
    }
    const next = afterReload(bundle.state, bundle.expired);
    if (next === "keep") return;
    const receipt = next === "recover" ? await this.#d.api.receipt(l.id).catch(() => undefined) : undefined;
    const replaces = next === "live" ? await this.#replaced(bundle) : undefined;
    const copy = [...this.view.body.querySelectorAll<HTMLElement>(".bank[data-bundle]")].filter((c) => c.dataset["bundle"] === l.id).at(-1);
    if (copy === undefined || epoch !== this.#epoch || (next === "recover" && receipt === undefined)) return;
    const path = this.#drafts.path === "unknown" ? undefined : this.#drafts.path;
    const turn: Turn = { said: l.said, channel: l.channel, draft: path === "fixture" ? this.#drafts.draftOf(l.said) : undefined, path };
    let live: Live | undefined;
    const card = this.#makeCard(bundle, turn, () => live, replaces);
    const moves = next === "live" || receipt?.state === "awaiting_step_up";
    live = {
      card,
      bundle,
      turn,
      options: undefined,
      reg: undefined,
      // The balances before it ran, for the success sheet — read now, while nothing has moved yet.
      before: moves && bundle.signing_payload.schedule === undefined ? this.#snapshot() : Promise.resolve(undefined),
      expiry: undefined,
      stepUp: undefined,
      removed: [],
    };
    if (next === "expired") card.expire();
    else if (next === "cancelled") card.cancelled();
    else if (receipt !== undefined) {
      if (balanceChanged(receipt)) card.balanceChanged();
      else card.signed(receipt);
    }
    copy.replaceWith(card.element);
    if (receipt?.state === "awaiting_step_up" && !balanceChanged(receipt)) this.#stepUp(live, receipt);
    if (next !== "live") return;
    // One live card at a time is the newest's rule for NEW cards; what was live before the reload is live again, beside.
    this.#cards.add(live, this.#cards.newest !== undefined);
    const now = this.#d.bankNow?.();
    this.#arm(live, now === undefined ? undefined : Date.parse(bundle.signing_payload.expires_at) - now.getTime());
    void this.#prepare(live);
  }

  /**
   * An account's action (§C.5): the chat with that account already named in the field —
   * " from Everyday Savings" — and the caret before it, so the customer says who and how much.
   */
  prefill(context: string): void {
    this.view.composer.setValue(context);
    this.view.composer.focus();
    this.view.composer.input.setSelectionRange(0, 0);
  }

  /** A tap on the mark: the field focused, the mic idle. A hold or a slide up (§C.3): already listening. */
  open(entry: "text" | "voice"): void {
    this.#opener = undefined;
    if (entry === "voice" && this.#voice.available && !this.#drafting) this.#startListening();
    else this.view.composer.focus();
  }

  /**
   * DCTA-176 F47 (§F.2 24d; B58, B61): back in the chat after the refresh — the bank's line (sent only when money had
   * stopped), and "Ask again" with the customer's own words under it, exactly as heard. A tap sends those words back as a
   * new turn (`source: reask`): with the gate passed, the bank now asks what it must — eligibility, then the question.
   * `bundleId` (DCTA-198, 176-yy): the refresh's signed card — its end is the bank's to read aloud (24d when the refresh
   * lifted a stop; 204, nothing, when the check was only due), when Read aloud applies.
   */
  kycDone(kyc: KycConfirmed, bundleId?: string): void {
    // The refusal's "Update my details" has done its work: it goes quiet, so one offer stands at a time (Ask again).
    this.view.retireOffers();
    const line = kyc.message;
    if (line !== undefined && line !== "") this.view.message([line]);
    const words = kyc.reask?.transcript;
    if (words !== undefined && words.trim() !== "") {
      // DCTA-198 (P2-F1): the words go back the way they arrived — never as typed by default.
      this.view.actions([{ label: "Ask again", small: `“${words}”`, name: `Ask again: ${words}`, once: true, onSelect: () => void this.say(words, reaskChannel(kyc.reask), "reask") }]);
    }
    // spoken.md §3.8: 24d is the bank's to speak — by the refresh card's end (B: `{part: "end", bundle_id}`), as any card's.
    if (bundleId !== undefined) this.#speech.speak({ part: "end", bundle_id: bundleId }, { now: true });
  }

  /**
   * DCTA-176: back from a layer over the chat (the refresh) — focus returns to the conversation's newest way on still open
   * (Ask again; or the chip that opened the layer, after Not now or Close), else to the field. Never left on the page.
   */
  returnFocus(): void {
    // DCTA-209 (WCAG 2.4.3): back from a schedule — the View or the read card's row that opened it.
    const opener = this.#opener;
    this.#opener = undefined;
    if (opener?.isConnected === true) {
      opener.focus({ preventScroll: true });
      return;
    }
    const open = [...this.view.body.querySelectorAll<HTMLButtonElement>(".opts:not([data-spent]) .opt")].at(-1);
    if (open !== undefined) open.focus({ preventScroll: true });
    else this.view.composer.focus();
  }

  /** DCTA-209 (27m, 29b): a schedule over the chat — a signed card's View, a read card's row; its opener gets focus back. */
  #openSchedule(mandateId: string): void {
    const a = document.activeElement;
    this.#opener = a instanceof HTMLElement && this.view.body.contains(a) ? a : undefined;
    this.#d.onSchedule?.(mandateId);
  }

  // ------------------------------------------------------------------ voice
  /** The mic tapped (or Enter on it): listen, tap mode — or, while listening, that is done. */
  #toggleMic(): void {
    if (this.#voice.state === "listening") {
      this.#act((eos, at) => eos.done(at));
      return;
    }
    this.#startListening("tap");
  }

  /**
   * DCTA-199 ruling (Claude, 13 Oct): a card a passkey signs outside the conversation — 17 Payees' add, the identity check's
   * declaration — speaks as every card does: its read-back by reference, through this chat's Read aloud (its choice and its
   * last turn), instead of whatever is still being read.
   */
  readCard(bundleId: string): void {
    this.#speech.speak({ part: "readback", bundle_id: bundleId }, { now: true });
  }

  /**
   * Claude's ruling 3 (P3-B4): "Show the earlier card" — the kept card where it stands in this conversation, brought into
   * view with its header focused (no motion: it simply comes into view); a card this conversation does not hold opens at
   * its receipt in Activity.
   */
  #showKept(bundleId: string): void {
    const card = this.view.body.querySelector<HTMLElement>(`.bank[data-bundle="${CSS.escape(bundleId)}"]`);
    const head = card?.querySelector<HTMLElement>(".bh");
    if (card === null || head === null || head === undefined) {
      this.#d.onReceipt?.(bundleId);
      return;
    }
    card.scrollIntoView({ block: "start" });
    if (head.tabIndex < 0) head.tabIndex = -1;
    head.focus({ preventScroll: true });
  }

  #consentGone(): void {
    this.#consent = "gone";
    this.view.consentNote(null);
  }

  /** DCTA-162 F31 — chat-level: the bank stops reading (Me's control, leaving the chat). */
  silence(): void {
    this.#speech.stop();
  }

  /**
   * DCTA-141: one listening = one end-of-speech module. Tap (the mic, the mark's hold, "Say it again"): until Done
   * or adaptive silence. Hold (the mic held): until the release. Large type (Mdm Lim): silence never sends.
   */
  #startListening(mode: EosMode = "tap"): void {
    // F31: the microphone stops the bank's voice at once; inside its tap the speaker is unlocked (iOS) for the answer.
    this.#speech.unlock("mic");
    this.#speech.stop();
    if (!this.#voice.available || this.#drafting || this.#voice.state === "listening") return;
    this.#voiceCancelled = false;
    this.#closing = undefined;
    this.#listenAt = performance.now();
    this.#firstChunkAt = undefined;
    this.#eos = new EndOfSpeech({ mode, senior: this.#d.root.getAttribute("data-type") === "large", at: this.#listenAt });
    // DCTA-191: a new listening — a new clip.
    this.#d.audio?.clear();
    this.view.quiet(true);
    this.view.composer.listen(mode);
    this.#engineMic = false;
    this.#voice.start();
    // An engine that could not start has already ended the listening (#listeningEnded).
    if (!this.#listening()) return;
    this.#engineMic = this.#voice.levels;
    if (this.#consent === "sent") this.#consentGone();
    if (this.#consent === "unseen" && this.#engineMic) {
      this.#consent = "showing";
      this.view.consentNote(VOICE_CONSENT);
    }
    const bars = (levels: number[]): void => this.view.composer.levels(levels);
    // DCTA-181: an engine that holds the microphone (Tencent's) drives the bars with its own chunks — never a second
    // microphone beside it; the browser's engine keeps the meter (or, on iOS, the words).
    if (this.#shareMic && !this.#engineMic) void this.#level.start(12, bars);
    else this.#pulse.start(12, bars);
    this.#tickEos();
  }

  /** A session is open (read afresh: `start()` can end it before it returns). */
  #listening(): boolean {
    return this.#voice.state === "listening";
  }

  /** Every listening ends here: the meter's microphone and audio context released, the pulse stopped. */
  #releaseMic(): void {
    this.#level.stop();
    this.#pulse.stop();
  }

  #heardWords(text: string, final: boolean): void {
    const eos = this.#eos;
    if (this.#voiceCancelled || eos === undefined) return;
    this.#pulse.kick();
    eos.heard(text, final, performance.now());
    // The words grow on screen — after a send is decided too: the engine's last words land before they go.
    if (eos.transcript !== "") this.view.interim(eos.transcript);
    this.#tickEos();
  }

  /** The end-of-speech module at this instant: an ending, or the phase to show and the next wake. */
  #tickEos(): void {
    const eos = this.#eos;
    if (eos === undefined || eos.ended !== undefined) return;
    const at = performance.now();
    const ending = eos.tick(at);
    if (ending !== undefined) {
      this.#finish(ending);
      return;
    }
    const phase = eos.phase(at);
    const wake = eos.wake(at);
    // In the countdown the next wake is the send itself: the ring drains over what is left.
    this.view.composer.phase(phase, phase === "countdown" && wake !== undefined ? wake - at : 0);
    if (this.#eosTimer !== undefined) clearTimeout(this.#eosTimer);
    this.#eosTimer = wake === undefined ? undefined : setTimeout(() => this.#tickEos(), Math.max(0, wake - at) + 5);
  }

  /** The customer's own act (Done, the release, a tap on the words) on the listening in progress. */
  #act(act: (eos: EndOfSpeech, at: number) => Ending): void {
    const eos = this.#eos;
    if (eos === undefined || eos.ended !== undefined || this.#voice.state !== "listening") return;
    this.#finish(act(eos, performance.now()));
  }

  /** The system took the hold's pointer: listening goes on as a tap — never a send. */
  #holdLost(): void {
    const eos = this.#eos;
    if (eos === undefined || eos.ended !== undefined) return;
    eos.tap(performance.now());
    this.view.composer.tapMode();
    this.#tickEos();
  }

  /**
   * Listening is over. DCTA-181 — Done never discards speech: the engine finishes (what it still holds goes to Tencent,
   * its last words arrive, within END_WATCHDOG_MS) and the words are settled only then (`#listeningEnded`). Processing
   * from this moment (DCTA-75 P1-1). The one act that takes the words as they are on screen is a tap on them: the engine
   * is dropped at once.
   */
  #finish(ending: Ending): void {
    if (this.#eosTimer !== undefined) clearTimeout(this.#eosTimer);
    this.#eosTimer = undefined;
    this.#releaseMic();
    this.#closing = ending;
    if (this.#voice.state !== "listening") {
      this.#listeningEnded();
      return;
    }
    if (ending.why === "edit") {
      this.#voice.cancel();
      return;
    }
    this.view.lockInterim();
    this.view.composer.process();
    this.#voice.stop();
  }

  #cancelListening(): void {
    this.#voiceCancelled = true;
    this.#d.audio?.clear();
    if (this.#eosTimer !== undefined) clearTimeout(this.#eosTimer);
    this.#eosTimer = undefined;
    this.#voice.cancel();
    this.view.dropInterim();
    this.view.quiet(false);
    this.#releaseMic();
    this.view.composer.idle();
  }

  /** The session is over: the app's decision, the customer's Cancel, or the engine gone for good (a refused restart, an error). */
  #listeningEnded(): void {
    if (this.#eosTimer !== undefined) clearTimeout(this.#eosTimer);
    this.#eosTimer = undefined;
    this.#releaseMic();
    const end = this.#voice.lastEnd ?? "";
    // How the engine ended (an error code, "end", "cancel", "timeout", "restart-failed") and (DCTA-181) how soon after the
    // tap the microphone gave its first chunk, or "none" — for the device notes, never shown as copy.
    this.view.composer.element.dataset["voiceEnd"] = end;
    const dataset = this.view.composer.element.dataset;
    if (!this.#engineMic) delete dataset["voiceMic"];
    else dataset["voiceMic"] = this.#firstChunkAt === undefined ? "none" : String(Math.round(this.#firstChunkAt - this.#listenAt));
    const eos = this.#eos;
    const decided = this.#closing;
    this.#eos = undefined;
    this.#closing = undefined;
    this.view.quiet(false);
    // After the listening, never during it: a screen reader reading the line would reach the microphone.
    if (this.#engineNote) {
      this.#engineNote = false;
      this.view.note(ENGINE_ASIDE);
    }
    if (this.#voiceCancelled) {
      this.#voiceCancelled = false;
      return;
    }
    if (eos === undefined) return;
    // Not decided by the app: the engine stopped for good — the words wait for review, never sent on their own.
    const ending = decided ?? eos.lost(performance.now());
    // A tap on the words takes them exactly as shown; every other end is settled on the engine's LAST result (DCTA-181).
    const words = ending.why === "edit" ? ending.text : eos.transcript.trim();
    let kind = settle(ending, words);
    // Words the engine never confirmed — its stable sentence did not come in time ("timeout"), or it failed after the
    // act — are never sent on their own: they wait in the field (19's third rule).
    if (kind === "send" && end !== "end") kind = "review";
    if (kind === "review") {
      this.view.dropInterim();
      this.view.composer.review(words, { focus: ending.why === "edit" });
      return;
    }
    if (kind === "nothing") {
      this.#cantHear();
      return;
    }
    // Processing covers the whole wait — the draft, the policy, the card — and ends the
    // moment the answer is drawn (DCTA-75 P1-1: a fixed 600 ms timer let it go idle
    // mid-wait on a slow bank, or linger on a card already on screen). The sign button's
    // own aria-busy covers the assertion options that follow.
    this.view.composer.process();
    // DCTA-162 (§1.1): a spoken turn — the bank's answer is read aloud in "When I speak".
    this.#speech.turn("voice");
    void this.say(words, "voice").finally(() => this.view.composer.idle());
  }

  /**
   * 19j, once (DCTA-181 × DCTA-178: "a no-speech or failed recognition shows ONE quiet line, never repeated; retry stays
   * silent"): while the last one is still the newest line in the conversation, a second miss adds nothing and says
   * nothing — the field and the mic are ready for the retry.
   */
  #cantHear(): void {
    this.view.dropInterim();
    this.view.composer.idle();
    const last = this.#cantHearLine;
    if (last !== undefined && this.view.isNewest(last)) {
      // Not drawn again, not read aloud again — but a screen reader is told (WCAG 4.1.3).
      this.view.composer.status(CANT_HEAR);
      return;
    }
    this.#cantHearLine = this.view.assistant(CANT_HEAR);
    // spoken.md §3.3: the customer spoke — 19j is read aloud, by its row (the bank holds the words).
    this.#speech.turn("voice");
    this.#speech.speak({ part: "reply", line: "19j" });
  }

  #micOff(): void {
    if (this.#eosTimer !== undefined) clearTimeout(this.#eosTimer);
    this.#eosTimer = undefined;
    this.#eos = undefined;
    this.#closing = undefined;
    this.#releaseMic();
    this.view.quiet(false);
    this.view.dropInterim();
    this.view.composer.disable();
  }

  // ------------------------------------------------------------------- send
  /**
   * The customer's words, and how they came (DCTA-111): `voice` from the microphone, `typed` from
   * the field — and from a phrase the app offered and the customer tapped (exact words, no
   * recognition in between). The bank is told with every utterance; it carries no authority.
   */
  async say(text: string, channel: Channel = "typed", source?: "reask"): Promise<void> {
    if (this.#drafting) {
      // One request at a time; the words go back into the field rather than away.
      if (this.view.composer.value.trim() === "") this.view.composer.setValue(text);
      return;
    }
    // DCTA-162 (F31): a new request — what the bank was reading stops. Whether its answer is read aloud is the turn's
    // (the composer's Send, the end of a listening); a chip's words are not a turn and change nothing.
    this.#speech.stop();
    // Claude's ruling 2: the voice-clip line stays through the first words after it, and goes with the next turn.
    if (this.#consent === "showing") this.#consent = "sent";
    else if (this.#consent === "sent") this.#consentGone();
    const offered = this.#qs.length === 0 ? await this.#offerWords(text, channel) : "bank";
    if (offered === "done") return;
    this.#drafting = true;
    this.#epoch += 1;
    // DCTA-191: the words take the listening's clip — a spoken new request sends it as its evidence; anything else drops it.
    const clip = this.#d.audio?.take();
    this.view.collapseGreeting();
    const read = this.#reads.match(text);
    // A new request: card details go back under their mask, an open offer goes quiet. DCTA-189 (QA DW-2): a read-only
    // one leaves the identity check's way on live ("What's my balance?", then "Update my details" still opens the refresh).
    this.#hideDetails();
    this.view.retireOffers({ read: read !== undefined });
    // DCTA-162: a voice turn says "You said" above the words as they were heard (DCTA-226: drawn already when the words
    // were asked at an offer first).
    if (offered !== "drawn") this.view.me(text, { said: channel === "voice" });
    try {
      if (read !== undefined) {
        // A new request: the open question and the live card are left behind (one live card at a time).
        this.#closeQuestion();
        this.#retireLive();
        this.#inFlight = { said: text, channel };
        await this.#read(read);
        return;
      }
      // DCTA-189: with two questions on the card, the words answer the one whose chip or figure they name (else the first).
      const open = this.#qs[answerTarget(text, this.#qs.map((o) => o.question))];
      if (open !== undefined && !this.#drafts.holds(text)) {
        // An answer in words to the open question (04): it names one option, a
        // figure where one is asked for — or nothing, and the question is asked again.
        // QA-04: an answer interrupted by a reload is asked again from its question's own words.
        this.#inFlight = { said: open.turn.said, channel: open.turn.channel };
        await this.#answerInWords(text, open, channel);
        return;
      }
      // A new request: the open question, if any, is left behind, answered by nothing.
      this.#closeQuestion();
      // DCTA-188: with judge mode's faulty-drafter switch on, the words go to the demo route whatever this bank drafts on.
      if (this.#drafts.path === "fixture" && !this.#drafts.holds(text) && !this.#drafts.demoArmed) {
        this.view.assistant(NOT_YET);
        return;
      }
      await this.#request(text, channel, source, channel === "voice" && source === undefined ? clip : undefined);
    } finally {
      this.#drafting = false;
      this.#inFlight = undefined;
    }
  }

  /**
   * DCTA-139 (§F.2 16d, 16h, 16i — F15, F14): an offer open (chips used once) and no question — the customer's words
   * answer the offer: a yes picks its one chip, as a tap does (the chip's own action draws its words); a decline on its
   * own ends it, "Nothing was sent."; words that name no chip and start no request are asked about once (16h), and the
   * same again lets the offer go (16i). A request (a verb, a protective word, a read) is not an answer: "bank".
   * DCTA-226 (§5.7): words that ask something, beside an offer the bank holds, go to that offer first (`#askAtOffer`); the
   * bank's answer is drawn and the offer stays open. Not answered there: what the words did before — a new request ("drawn":
   * the customer's words are on screen already) or asked about here.
   */
  async #offerWords(text: string, channel: Channel): Promise<"done" | "bank" | "drawn"> {
    const offer = this.view.openOffer();
    if (offer === undefined || this.#reads.match(text) !== undefined) return "bank";
    const reply = offerReply(text, offer.labels);
    const asks = reply.kind === "question" ? offer.asks : undefined;
    const before = reply.kind === "question" ? reply.otherwise : reply.kind;
    if (asks === undefined && before === "request") return "bank";
    this.view.collapseGreeting();
    if (reply.kind === "pick") {
      offer.pick(reply.index);
      return "done";
    }
    this.view.me(text, { said: channel === "voice" });
    if (asks !== undefined) {
      if (await this.#askAtOffer(text, channel, offer, asks)) return "done";
      if (before === "request") return "drawn";
    }
    if (before === "decline") {
      this.view.retireOffers();
      this.view.assistant(SAY.notSent);
    } else if (offer.reasked) {
      this.view.retireOffers();
      this.view.assistant(LET_GO);
    } else {
      offer.reask();
      this.view.assistant(reaskLine(offer.labels));
    }
    return "done";
  }

  /**
   * DCTA-226 (§5.7): words that ask, to the bank's open offer — `POST /v1/drafts/{draftId}/answer {question_id, words}`. Its
   * answer is drawn as any answer is (the bank's reply, verbatim and said; a refusal), and the offer's chips stay live when
   * the bank kept it open (`offerKept`). False: the bank does not answer these words at an offer (400) — nothing drawn.
   */
  async #askAtOffer(text: string, channel: Channel, offer: OpenOffer, target: OfferTarget): Promise<boolean> {
    const turn: Turn = { said: text, channel, draft: undefined, path: undefined };
    let declined = false;
    // One request at a time (§D.9): the offer's chips hold while the bank answers — a tap meanwhile neither sends nor spends.
    this.#drafting = true;
    offer.hold(true);
    try {
      await this.#run(() => this.#d.api.answer(target.draftId, { question_id: target.questionId, words: text }), turn, { answering: true, atOffer: { declined: () => (declined = true) } });
    } finally {
      offer.hold(false);
      this.#drafting = false;
    }
    return !declined;
  }

  /** A new request: the words to the bank, by whichever path the bank drafts on. */
  async #request(said: string, channel: Channel, source?: "reask", audio?: Uint8Array): Promise<void> {
    const turn: Turn = { said, channel, draft: undefined, path: undefined, ...(source === undefined ? {} : { source }), ...(audio === undefined ? {} : { audio }) };
    await this.#run(() => this.#drafts.send(turn), turn);
  }

  /**
   * One round trip to the pipeline — a sentence or an answer — and what the customer sees of
   * its outcome: a card, a question, a refusal. The working row holds the place meanwhile (04's
   * `thinking`; DCTA-111: a line and Cancel from 2.5 s, the reassurance from 10 s), and the app
   * stops waiting at 45 s (`app/waiting.ts`; DCTA-124: past the bank's own 40 s bound). Cancel and the give-up are the customer's end of
   * the wait, not the bank's: what the bank answers afterwards is `#late`.
   */
  async #run(call: () => Promise<DraftOutcome>, turn: Turn, how: RunHow = {}): Promise<void> {
    // DCTA-221 review: every round trip is the customer moving on (a chip on a restored question too, not only words) — a
    // card a reload is still reading back stays as drawn, never live beside this turn's own card.
    this.#epoch += 1;
    // QA-04: the turn's own words while the bank answers it — the request's, or the question's for an answer.
    const before = this.#inFlight;
    this.#inFlight = { said: turn.said, channel: turn.channel };
    try {
      await this.#turn(call, turn, how);
    } finally {
      this.#inFlight = before;
    }
  }

  async #turn(call: () => Promise<DraftOutcome>, turn: Turn, how: RunHow): Promise<void> {
    let cancel = (): void => undefined;
    const row = this.view.working(() => cancel());
    const drafted = call();
    const work = drafted.then(async (outcome) => {
      // DCTA-136 (F10): the bundle read, with its own answer's flags when the read carries none (surfaces.withFlags).
      // DCTA-160b: and the steps it left out of the card (surfaces.withSkipped); DCTA-221 (row 27p): and the sentence about a
      // time of day it did not keep (surfaces.withNote) — all ride on the answer, not the read.
      const bundle = outcome.bundle_id === undefined ? undefined : withNote(withSkipped(withFlags(await this.#d.api.bundle(outcome.bundle_id), outcome), outcome), outcome);
      return {
        outcome,
        bundle,
        // DCTA-136 (F19): an account question the bank sent without chips gets the customer's accounts, from its read.
        read: outcome.bundle_id === undefined ? await this.#readChips(outcome) : [],
        // DCTA-209 (27j): a schedule card that replaces one says which — route 2's read of the signed `supersedes`, before the
        // card is drawn (no notice arriving under the customer's thumb).
        replaces: await this.#replaced(bundle),
      };
    });
    const waiting = wait(work, { onPhase: (phase) => row.phase(phase) });
    cancel = () => waiting.cancel();
    const ended = await waiting.done;
    try {
      switch (ended.kind) {
        case "answered": {
          const { outcome, bundle, read, replaces } = ended.value;
          // DCTA-188 Change 4: the faulty-drafter demo caught nothing — judge mode's one line, the bank's label above it.
          if (outcome.kind === "demo_result") {
            this.view.judgeLine(demoResultLine(outcome) ?? DEMO_FAULT_LINES.nothingToSwap, row.element);
            this.#demoLabel(outcome);
            return;
          }
          if (outcome.timings !== undefined || outcome.model_timings !== undefined) this.#timings = { draft: stageTimings(outcome) };
          // The cards the bank cancelled with this answer are never left signable here (DCTA-159 ask 1).
          this.#retireNamed(outcome.cancelled_bundle_ids, bundle !== undefined ? "replaced" : "cancelled");
          this.#rebound = false;
          if (bundle !== undefined) {
            // DCTA-222: the bank's answer to a how-question asked beside the errand first; DCTA-203: then a read the bank ran
            // beside the card ("what's my balance and send …") — the bank's answer, then the card.
            let lead = this.#explain(outcome, row.element);
            for (const r of bankReadsOf(outcome.executed)) {
              await this.#read(r, lead);
              lead = this.view.typing();
            }
            this.#present(bundle, turn, lead, how.scoped === true, keptLine(outcome), replaces);
          }
          // DCTA-221: the card's questions the customer just answered from (the one answered, the ones waiting) — one the bank
          // asks again is drawn marked as still open; after the customer's own words it is drawn, never re-bound in silence.
          else
            await this.#otherOutcome(outcome, row.element, turn, how.scoped === true, read, {
              byWords: how.byWords === true,
              seen: how.question === undefined ? [] : [how.question.question, ...this.#waiting.map((w) => w.question)],
            });
          // DCTA-226 (§5.7): words asked at an offer — the offer ends unless the bank kept it open.
          if (how.atOffer !== undefined && !offerKept(outcome)) this.view.retireOffers();
          // DCTA-231 V2: a tightening the bank ran (never a read it answered) changed the bank — Home and Invest read again.
          // MUTATION TARGET: without it the customer who locked goes back to a Home still drawn unlocked.
          if ((outcome.executed ?? []).some((e) => e.read === undefined)) this.#d.onBankChanged?.();
          // DCTA-188 Change 3: a card or question the silent re-check cleared says so to judge mode, beside its lead.
          this.#recheckNote(outcome);
          // DCTA-188 Change 4: an answer of the faulty-drafter demo (a firewall refusal) says it was staged, above it.
          this.#demoLabel(outcome);
          // DCTA-162 (F30): what the screen just drew, in its order, read aloud when the policy says so — by reference.
          // DCTA-189: a re-bind drew nothing new (the question was on the card already, and was read with it).
          if (!this.#rebound) for (const ref of outcomeParts(outcome, bundle)) this.#speech.speak(ref);
          // F19: the customer's own words the firewall refused as an answer leave the question open at the bank — it is asked
          // again (§F.2 20i). F22 (20j): a chip or a typed figure refused the same way was refused for the question's own
          // message — the question stays spent, under row 1's message and its "Talk to us"; nothing is asked again.
          if (how.question !== undefined && how.answered !== undefined && bundle === undefined && reasksAfterBlock(outcome, how.answered)) {
            this.#reopen(how.question, undefined, row.element, "blocked");
            this.#speakQuestion(how.question);
          }
          // DCTA-189: a chip tapped on the card's other question while the bank answered — its question came back (open
          // again after a re-bind): sent now. DCTA-221: or drawn again as its own turn (after words) — the tap answers it there,
          // its chip pressed in the new group; the card's questions end first (the held one's chip is no longer pressed there).
          const held = this.#queued;
          this.#queued = undefined;
          const target = held === undefined ? undefined : (this.#qs.find((o) => o === held.open) ?? this.#qs.find((o) => sameQuestion(o.question, held.open.question)));
          if (held !== undefined && target !== undefined) {
            if (target !== held.open) {
              this.#endWaiting();
              target.handle.spend(held.index);
            }
            this.#waitOthers(target);
            this.view.me(held.reply.label);
            await this.#answer(target, held.reply);
          }
          return;
        }
        case "failed":
          if (ended.error instanceof NoFixtureDraft) {
            this.view.assistant(NOT_YET, row.element);
            return;
          }
          // DCTA-226 (§5.7): the bank does not answer these words at an offer (nothing claimed) — the caller does what the words
          // did before; nothing of this round trip stays on screen.
          if (how.atOffer !== undefined && answerRefused(ended.error) !== undefined) {
            row.element.remove();
            how.atOffer.declined();
            return;
          }
          this.#failed(ended.error, turn, row.element, how.answering === true, how.question);
          // DCTA-188 Change 4: the faulty-drafter demo's freeze says it was staged — the bank's label above the sentence.
          if (ended.error instanceof ApiError) this.#demoLabel(ended.error.body);
          return;
        case "cancelled":
        case "timed-out":
          this.view.assistant(ended.kind === "cancelled" ? CANCELLED : TOO_LONG, row.element);
          // spoken.md §3.3: the give-up (3e) is read aloud, by its row; a Cancel was the customer's own act.
          if (ended.kind === "timed-out") this.#speech.speak({ part: "reply", line: "3e" });
          this.#giveBack(turn);
          void this.#late(drafted);
          return;
      }
    } finally {
      // DCTA-189: the card's other questions came back as they were (re-bound above), or they end here with the answer.
      this.#endWaiting();
    }
  }

  /**
   * The words of a request that did not finish go back into the field, unsent — to send again or
   * change (a voice request's words can be corrected before they go). Never over words the
   * customer has started typing meanwhile. On a keyboard the caret follows them; a phone keeps
   * its keyboard down (the customer tapped Cancel, not the field).
   */
  #giveBack(turn: Turn): void {
    if (this.view.composer.value.trim() !== "") return;
    this.view.composer.setValue(turn.said);
    if (globalThis.matchMedia?.("(pointer: coarse)").matches !== true) this.view.composer.focus();
  }

  /**
   * The customer stopped waiting (Cancel, or the app gave up) and the bank answers anyway. A card
   * nobody waited for is cancelled at the bank, so nothing is left that could be signed. A
   * protective action the bank already ran (a tightening runs without a signature) is shown: the
   * screen never hides the bank's state. A question or a refusal moved nothing and is let go.
   */
  async #late(drafted: Promise<DraftOutcome>): Promise<void> {
    let outcome: DraftOutcome;
    try {
      outcome = await drafted;
    } catch {
      return;
    }
    if (outcome.bundle_id !== undefined) {
      void this.#d.api.cancelBundle(outcome.bundle_id, "the customer stopped waiting before it was shown").catch(() => undefined);
      return;
    }
    const lines = (outcome.executed ?? []).map((e) => e.result_summary).filter((t): t is string => t !== undefined && t !== "");
    if (lines.length > 0) this.#bankLines(lines);
  }

  // --------------------------------------------------------------- 04 clarify
  /**
   * The bank's question, its chips under it, held open (04). DCTA-136 (F19): a question that takes words (DCTA-135: every
   * question about a field) is answered by the customer's next words, typed or said, chips or none. `read`: chips the
   * app drew from the bank's own read for an account question that came without any (`clarify.accountChips`). `lead`:
   * the bank's sentence when it refused an answer and kept the question (`#reopen`). DCTA-189 (QA DW-4): every question
   * of the turn the customer can answer by its own id is drawn on ONE card ("Two Johns are saved. Which one?" · "And
   * fifty — fifty dollars, or fifty thousand?"); asked again (16h, a refusal kept it), only that one. The card's other
   * question, asked again by the bank after one is answered by a chip, keeps its place (`#rebind`). DCTA-221: after the
   * customer's own words (`byWords`) it is drawn again as its own turn — every answer gets a visible reply — and a question
   * the customer has just seen on the card (`seen`) is marked as still open wherever it is drawn again.
   */
  #ask(
    outcome: DraftOutcome,
    dots: HTMLElement,
    turn: Turn,
    opts: {
      /** Asked again in other words (§F.2 16h). */
      readonly text?: string;
      readonly lead?: string | undefined;
      readonly read?: readonly ReadChip[];
      /** This is the question asked once more: the next miss lets it go. */
      readonly again?: boolean;
      /** Draw R-U1's "Add a payee" offer under it (8b) — not when a refusal's own way on (Talk to us) stands just above. */
      readonly pointer?: boolean;
      /** DCTA-221: the customer answered in their own words — no re-bind in silence: the bank's reply is drawn and said. */
      readonly byWords?: boolean;
      /** DCTA-221: the questions of the card the customer just answered from — one asked again is marked as still open. */
      readonly seen?: readonly Clarification[];
    } = {},
  ): void {
    const question = answerable(outcome);
    const first = outcome.clarifications?.[0];
    const wording = opts.text ?? first?.question_text;
    if (wording === undefined) {
      this.view.assistant(NOT_YET, dots);
      return;
    }
    if (question === undefined) {
      // No id, nothing to answer it by: the question is said, and the next sentence is a new request.
      this.view.question(wording, [], dots, opts.lead);
      if (opts.pointer !== false) this.#addPayeeChip(first);
      return;
    }
    const fresh = opts.text === undefined && opts.again !== true;
    const asked = fresh ? answerableAll(outcome) : [question];
    if (fresh && opts.byWords !== true && this.#rebind(asked, outcome.draft_id, dots)) return;
    const read = opts.read ?? [];
    const opens: OpenQuestion[] = [];
    const seen = fresh ? (opts.seen ?? []) : [];
    const handles = this.view.questions(
      asked.map((q, k) => ({
        text: k === 0 ? wording : (q.question_text ?? ""),
        options: this.#chipsFor(q, k === 0 ? read : [], () => opens[k]),
        still: seen.some((s) => sameQuestion(s, q)),
      })),
      dots,
      opts.lead,
    );
    asked.forEach((q, k) => {
      const handle = handles[k];
      if (handle !== undefined) opens.push({ draftId: outcome.draft_id, question: q, turn, handle, read: k === 0 ? read : [], reasked: opts.again === true });
    });
    this.#setOpen(opens);
    // DCTA-124 (F6): R-U1 takes the customer's next words as its answer — and keeps its chip (8b).
    if (opts.pointer !== false) this.#addPayeeChip(question);
  }

  /**
   * One question's chips (04), each answering ITS question (`open()`, set once the card is drawn): the bank's choices
   * (DCTA-143: its labels as sent, a payee never paid carries the marker), F19's accounts from the bank's read, "A
   * different amount" beside figures to pick (F42 — it only opens the field), "Don't send" for a number registered to
   * someone else (§F.2 6h).
   */
  #chipsFor(question: Clarification, read: readonly ReadChip[], open: () => OpenQuestion | undefined): ChipOption[] {
    const choices = question.choices ?? [];
    const other = differentAmount(question);
    const tap = (reply: Exclude<Reply, { kind: "unmatched" }>, index: number): void => {
      const o = open();
      if (o !== undefined) void this.#answerTap(o, reply, index);
    };
    return [
      ...choices.map((c, i) => ({ ...choiceChip(c), onSelect: () => tap({ kind: "choice", choiceId: c.choice_id, label: c.label }, i) })),
      ...read.map((c, i) => ({ label: c.label, onSelect: () => tap({ kind: "words", words: c.words, label: c.label }, choices.length + i) })),
      ...(other ? [{ label: DIFFERENT_AMOUNT, onSelect: () => this.view.composer.focus() }] : []),
      ...(nameDivergence(question)
        ? [
            {
              label: SAY.dontSend,
              onSelect: () => {
                const o = open();
                if (o !== undefined) this.#dontSend(o, choices.length + read.length + (other ? 1 : 0));
              },
            },
          ]
        : []),
    ];
  }

  /**
   * DCTA-189: the bank answered one question of the card by asking the others again — under a new draft and new ids. When
   * they are the SAME questions (`sameQuestion`: field, words, chips, ways to answer), they keep their place on the card and
   * answer the new draft from now on: no new turn, the waiting row goes. A chip the customer tapped on one meanwhile is
   * sent next (`#queued`, by `#run`). Anything else is not a re-bind: the card's questions end and the new ones are drawn.
   */
  #rebind(asked: readonly Clarification[], draftId: string, dots: HTMLElement): boolean {
    const waiting = this.#waiting;
    if (waiting.length === 0 || waiting.length !== asked.length || !waiting.every((w, i) => sameQuestion(w.question, asked[i] as Clarification))) return false;
    waiting.forEach((w, i) => {
      w.draftId = draftId;
      w.question = asked[i] as Clarification;
    });
    this.#waiting = [];
    dots.remove();
    this.#rebound = true;
    this.#setOpen(waiting);
    return true;
  }

  /** DCTA-189: `open` is answered — the card's other questions wait for the bank's answer (their ids are this draft's). */
  #waitOthers(open: OpenQuestion): void {
    this.#waiting = this.#qs.filter((o) => o !== open);
    this.#setOpen([]);
  }

  /** DCTA-189: the card's other questions did not come back as they were — they end, nothing pressed (a held chip neither). */
  #endWaiting(): void {
    for (const w of this.#waiting) w.handle.spend();
    this.#waiting = [];
    this.#queued = undefined;
  }

  /**
   * F19: the bank refused an answer and still holds the question (`answerRefused` — "refused"; `reasksAfterBlock` —
   * "blocked", the firewall refused the customer's own words; F22: never a chip or a figure). The chips spent on the way
   * out go; the bank's sentence (when it sent one and it was not said already) comes first, then the SAME question by
   * the same id, its chips under it — so the next words answer it again, never a new message. Once only: a question
   * asked again that is refused again is let go
   * with the bank's sentence (DCTA-136 review: `CLARIFICATION_ANSWER_INVALID` is also the bank's answer when another
   * answer already took the question). After a block the refusal's own way on — Talk to us, the safe exit after an
   * injection — stays live, so R-U1's "Add a payee" is not drawn beside it (one open offer group).
   */
  #reopen(open: OpenQuestion, lead: string | undefined, row: HTMLElement, why: "refused" | "blocked"): void {
    if (open.reasked) {
      // §F.2 16k (F14): refused again — the bank's sentence, then the app's own line, and the question goes.
      this.#closeQuestion();
      this.view.message([...(lead === undefined || lead === "" ? [] : [lead]), START_OVER], { replacing: row });
      return;
    }
    // A chip answered (by keyboard or a screen reader): its group is about to go — focus moves to the new group, never to <body>.
    const hadFocus = open.handle.holdsFocus();
    open.handle.withdraw();
    if (why === "refused") this.view.retireOffers();
    this.#ask({ draft_id: open.draftId, kind: "clarification", policy_version: "", audit_ref: "", clarifications: [open.question] }, row, open.turn, {
      lead,
      read: open.read,
      again: true,
      pointer: why === "refused",
    });
    if (hadFocus && this.#question?.handle.focus() !== true) this.view.composer.focus();
  }

  /**
   * F19 (the coordinator, from Umakhan's iPhone: "Which account should I use…?" with no chips): the customer's own
   * accounts as chips, read from the bank (`GET /v1/accounts`) for an account question the bank sent without any. A
   * read that fails draws none — typing still answers the question.
   */
  async #readChips(outcome: DraftOutcome): Promise<readonly ReadChip[]> {
    const question = answerable(outcome);
    if (question === undefined || !isAccountQuestion(question) || question.accepts_words !== true || (question.choices?.length ?? 0) > 0) return [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      // A slow read must not hold a prompt question behind the 45 s give-up: past READ_CHIPS_MS it goes without chips.
      const late = new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), READ_CHIPS_MS);
      });
      const read = await Promise.race([this.#d.api.accounts(), late]);
      return read === undefined ? [] : accountChips(question, read.accounts);
    } catch {
      return [];
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  /**
   * §F.2 8b (DCTA-120): no saved payee is called that — the way in for someone new is the bank's
   * add-payee screen (its 12-hour wait), never a number read out in the chat (8c, AC-08.1).
   */
  #addPayeeChip(question: Clarification | undefined): void {
    const open = this.#d.onAddPayee;
    if (question !== undefined && unresolvedPayee(question) && open !== undefined) this.view.offer(SAY.addPayee.label, SAY.addPayee.small, () => open());
  }

  /**
   * §F.2 6h: "Don't send" — the question is spent, the customer's words are drawn, and nothing goes to the bank. DCTA-189:
   * the card's other question ends with it (nothing is sent for either).
   */
  #dontSend(open: OpenQuestion, index: number): void {
    if (this.#drafting || !this.#qs.includes(open)) return;
    this.#closeQuestion(open);
    open.handle.spend(index);
    this.view.me(SAY.dontSend);
    this.view.assistant(SAY.notSent);
  }

  /**
   * Every open question is left behind, its chips spent (a new request; DCTA-189: the whole card) — but `keep`, which its
   * caller ends its own way (a chip pressed, the chips withdrawn).
   */
  #closeQuestion(keep?: OpenQuestion): void {
    for (const o of this.#qs) if (o !== keep) o.handle.spend();
    this.#setOpen([]);
    this.#cardPick?.spend();
    this.#cardPick = undefined;
  }

  /**
   * A chip: it becomes the customer's own bubble, bound to the turn it answered. DCTA-189: on a card of two, the other
   * question waits for the bank's answer (`#waitOthers`); a chip tapped on it meanwhile is held — pressed — and sent the
   * moment the bank asks it again (`#rebind`, then `#run`): one request at a time, and no tap lost.
   */
  async #answerTap(open: OpenQuestion, reply: Reply, index: number): Promise<void> {
    if (reply.kind === "unmatched") return;
    if (this.#waiting.includes(open)) {
      if (this.#queued === undefined) {
        open.handle.spend(index);
        this.#queued = { open, reply, index };
      }
      return;
    }
    if (this.#drafting || !this.#qs.includes(open)) return;
    this.#drafting = true;
    try {
      open.handle.spend(index);
      this.#waitOthers(open);
      this.view.me(reply.label);
      await this.#answer(open, reply);
    } finally {
      this.#drafting = false;
    }
  }

  /**
   * Typed or spoken words while a question is open; the bubble is already drawn. DCTA-136 (F19): they are that
   * question's answer, by its id — an offered chip they name, a figure where one is asked for, or the words as said
   * where the question takes words (every question about a field since DCTA-135), for the bank's resolver to read.
   * Never a new, context-free message (Umakhan's iPhone: "My main account" lost its question and came back as a
   * greeting).
   */
  async #answerInWords(text: string, open: OpenQuestion, channel: Channel): Promise<void> {
    // §F.2 16d (F15): a decline on its own ends any open question — "Nothing was sent." (6h's handling, widened: its
    // "no" / "don't send" are two of them) — but the near miss's (15i), whose "no" the bank reads (B20).
    if (declines(text) && !nearMiss(open.question)) {
      // DCTA-189: the whole card — nothing is sent for either question.
      this.#closeQuestion();
      this.view.assistant(SAY.notSent);
      return;
    }
    const reply = interpret(text, open.question);
    // DCTA-84 A2: a protective request always wins, and words that start a request are one —
    // the open question is left behind (its chips spent), never asked again over them. On the
    // model path the new request goes to the bank; the fixture path holds no draft for it.
    // Words that name no offered option and start with a request verb ("Send fifty to Sarah") are new.
    if (newRequest(text, reply.kind === "choice" || reply.kind === "amount")) {
      this.#closeQuestion();
      if (this.#drafts.path === "model") await this.#request(text, channel);
      else this.view.assistant(NOT_YET);
      return;
    }
    if (reply.kind !== "unmatched") {
      open.handle.spend();
      this.#waitOthers(open);
      // DCTA-221: the customer's own words, whatever they read as (words, a figure, a chip's name): the reply is drawn.
      await this.#answer(open, reply, true);
      return;
    }
    // Words that name no option, for a question that takes no words (the bank would refuse them): asked once more here.
    if (open.reasked) {
      // §F.2 16i (F14): asked twice and still nothing it can name — let the question go honestly.
      this.#closeQuestion();
      this.view.assistant(LET_GO);
      return;
    }
    // §F.2 16h (F14): asked once more, in other words — how to go ahead with the one chip, or which one of several —
    // with the options under it; the first chips go. DCTA-189: the card's other question ends (the bank asks it again).
    this.#closeQuestion(open);
    open.handle.withdraw();
    const dots = this.view.typing();
    const labels = [...(open.question.choices ?? []).map((c) => c.label), ...open.read.map((c) => c.label)];
    this.#ask({ draft_id: open.draftId, kind: "clarification", policy_version: "", audit_ref: "", clarifications: [open.question] }, dots, open.turn, {
      text: reaskLine(labels),
      read: open.read,
      again: true,
    });
  }

  /** `byWords` (DCTA-221): the customer typed or said the answer — a chip's tap is not. */
  async #answer(open: OpenQuestion, reply: Exclude<Reply, { kind: "unmatched" }>, byWords = false): Promise<void> {
    const questionId = open.question.question_id as string;
    const body: AnswerBody =
      reply.kind === "choice"
        ? { question_id: questionId, choice_id: reply.choiceId }
        : reply.kind === "amount"
          ? { question_id: questionId, value: reply.value }
          : // DCTA-124 (F6), DCTA-136 (F19): the words as said — the bank's firewall reads them before anything else.
            { question_id: questionId, words: reply.words };
    await this.#run(() => this.#d.api.answer(open.draftId, body), open.turn, { answering: true, question: open, answered: body, byWords });
  }

  /**
   * A card to sign. `scoped` (DCTA-124): it answers an offer made beside another signable card — that
   * card stays live and untouched (DCTA-123 (oo)); any other new card retires every live card. `kept` (DCTA-198, P3-B4):
   * the bank's sentence that a correction kept the earlier, signed card — its own turn above the ready line, with "Show
   * the earlier card" (Claude's ruling 3) when the bank named the card.
   */
  #present(bundle: BundleView, turn: Turn, dots: HTMLElement, scoped = false, kept?: { readonly line: string; readonly bundleId?: string }, replaces?: MandateView): void {
    const revising = !scoped && this.#editing !== undefined;
    const ready = readyLine(bundle, revising);
    if (kept === undefined) this.view.assistant(ready, dots);
    else {
      // Claude's ruling 3: the bank's sentence is its own turn, with the way to the kept card; "Ready." leads the new card.
      this.view.message([kept.line], { replacing: dots });
      const id = kept.bundleId;
      if (id !== undefined) this.view.actions([{ label: SHOW_KEPT, onSelect: () => this.#showKept(id) }]);
      this.view.assistant(ready);
    }

    // Every button acts on THIS card's entry (`live`, set just below): with a scoped offer two cards
    // can be live at once, and a tap must never sign or change the other one.
    let live: Live | undefined;
    const card = this.#makeCard(bundle, turn, () => live, replaces);
    if (revising) card.revised();
    card.element.classList.add("enter");
    live = {
      card,
      bundle,
      turn,
      options: undefined,
      reg: undefined,
      // DCTA-209: a schedule card ends on its own sheet (nothing moves at signing) — no balances to read before it.
      before: bundle.signing_payload.schedule === undefined ? this.#snapshot() : Promise.resolve(undefined),
      expiry: undefined,
      stepUp: undefined,
      removed: [],
    };
    this.#presented(live, scoped, revising);
  }

  /**
   * One card for one bundle, its buttons acting on its own entry (`entry()`): drawn when the bank answers, and again for
   * each version an edit in place makes (DCTA-170), in the same place, on the same entry.
   */
  #makeCard(bundle: BundleView, turn: Turn, entry: () => Live | undefined, replaces?: MandateView): BankCard {
    const onReceipt = this.#d.onReceipt;
    return new BankCard(bundle, {
      onSign: () => {
        const live = entry();
        if (live !== undefined) this.#sign(live);
      },
      onEdit: () => {
        const live = entry();
        if (live !== undefined) this.#edit(live);
      },
      onReask: () => void this.say(turn.said, turn.channel),
      onScamYes: (question) => {
        const live = entry();
        if (live !== undefined) this.#paused(live, question);
      },
      onTalk: () => void this.#talk(),
      // §C.10's end-line actions: Signed → this bundle's receipt; Not sent → the same words again.
      ...(onReceipt === undefined ? {} : { onReceipt: () => onReceipt(bundle.bundle_id) }),
      onRetry: () => void this.say(turn.said, turn.channel),
      // DCTA-101 run 2: the balance moved (POL-BAL-PROJECTED) — the same payment, drawn again.
      onUpdate: () => {
        const live = entry();
        if (live !== undefined) void this.#update(live);
      },
      // DCTA-124: the end line's rule id is judge mode's only.
      judge: () => this.#d.judgeOn?.() === true,
      // DCTA-209 (27k): a schedule card's "Not now" — cancelled at the bank, ended as cancelled (silent, EN11).
      onNotNow: () => {
        const live = entry();
        if (live !== undefined) void this.#notNow(live);
      },
      // DCTA-209 (27m): a signed schedule's View — the schedule, pushed over the chat.
      ...(this.#d.onSchedule === undefined ? {} : { onView: (mandateId: string) => this.#openSchedule(mandateId) }),
    }, replaces === undefined ? {} : { replaces });
  }

  /**
   * DCTA-209 (27j): the schedule a schedule card replaces, as route 2 reads it — only for a payload that signs `supersedes`.
   * A read that fails leaves the card to say it replaces one, without naming it (bankcard.ts): never a guess.
   */
  async #replaced(bundle: BundleView | undefined): Promise<MandateView | undefined> {
    const id = bundle?.signing_payload.schedule?.supersedes;
    if (id === undefined) return undefined;
    return this.#d.api.mandate(id).catch(() => undefined);
  }

  /**
   * DCTA-209 (27k, §2.1 Actions): "Not now" on a schedule card — the bundle is cancelled at the bank so it can never be
   * signed, and the card ends as cancelled at the bank's time. Nothing is spoken (EN11). A cancel that does not reach the bank
   * still ends the card here: it expires on its own within minutes and nothing was signed.
   */
  async #notNow(live: Live): Promise<void> {
    if (!this.#cards.alive(live) || !live.card.signable) return;
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    live.expiry = undefined;
    this.#cards.drop(live);
    live.card.cancelled(this.#d.bankNow?.()?.toISOString());
    await this.#d.api.cancelBundle(live.bundle.bundle_id, "the customer chose Not now on a schedule card").catch(() => undefined);
  }

  #presented(live: Live, scoped: boolean, revising: boolean): void {
    const { card, bundle, turn } = live;
    // One live card at a time: the ones before are kept, collapsed, and can never be signed again —
    // here or on the server. A scoped offer's card retires nothing.
    const editing = this.#editing;
    for (const previous of this.#cards.add(live, scoped)) this.#retire(previous, revising && previous === editing ? "revised" : "retired");
    if (!scoped) this.#editing = undefined;
    // A step the policy refused is not on the signable card, and it is not
    // silently dropped either: it comes first as its own Not sent card with the
    // server's own sentence and rule id (07's refusal anatomy; run 2 draws the
    // next-step button) — so the card that can be signed stays the newest thing
    // in the stream, with its sign button at the bottom of the fold. Its offer, if
    // the bank made one, is scoped to that step (DCTA-123 (oo)): accepting it leaves
    // this card live.
    this.#refusals(bundle.refused_steps, turn, undefined, true);
    this.view.append(card.element);
    this.view.scrollToNewest();

    this.#arm(live);
    void this.#prepare(live);
  }

  /**
   * The payload lives 120 s (180 s in large type). Measured from now, not from
   * the device clock against expires_at, so a skewed phone clock cannot keep a
   * dead card alive; one second early rather than one second late. DCTA-170: each version an edit makes has its own.
   */
  #arm(live: Live, leftMs?: number): void {
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    live.expiry = undefined;
    const p = live.bundle.signing_payload;
    // QA-04: a card read back after a reload has lived part of its life — what is left, on the bank's clock.
    const ttl = leftMs ?? Date.parse(p.expires_at) - Date.parse(p.created_at);
    if (Number.isFinite(ttl) && (ttl > 0 || leftMs !== undefined)) {
      live.expiry = setTimeout(() => this.#expire(live), Math.max(0, ttl - 1000));
    }
  }

  /**
   * Before the tap (§C.11, research 11 snippet D): the options for the ceremony the Sign
   * tap will start are fetched while nothing waits on a gesture, so the tap's first
   * statement is `get()` or `create()`. The bank's credential list decides which: known →
   * the payload's assertion options; not known → registration options and the line that
   * says what the one tap will do. The button holds (busy, label kept) until they land.
   */
  async #prepare(live: Live): Promise<void> {
    const card = live.card;
    card.busy(true);
    try {
      if (!webAuthnAvailable()) return; // Sign opens "not available on this phone"
      if (await this.#bankKnows()) {
        try {
          live.options = await this.#d.api.assertionOptions(live.bundle.bundle_id);
          return;
        } catch (error) {
          // The list said yes and the bank now says no (a credential removed meanwhile): create one.
          if (!(error instanceof ApiError && error.reasonCode === "CREDENTIAL_UNKNOWN")) throw error;
          this.#known = undefined;
        }
      }
      live.reg = await registrationOptions(this.#d.api);
      card.line(FIRST_PAYMENT);
    } catch (error) {
      if (error instanceof ApiError && /EXPIRED/.test(error.reasonCode)) this.#expire(live);
      else this.#error(error);
    } finally {
      if (this.#cards.alive(live)) card.busy(false);
    }
  }

  /**
   * The bank's own list (GET /v1/webauthn/credentials); a failed read tries signing. A yes is kept for the
   * session; a no is asked again next card — a passkey can be made outside the chat meanwhile (17's Check
   * and sign, DCTA-124), and a stale no would offer to save a second one.
   */
  #bankKnows(): Promise<boolean> {
    const known = (this.#known ??= this.#d.api.credentials().then(
      (r) => r.credentials.length > 0,
      () => true,
    ));
    void known.then((yes) => {
      if (!yes && this.#known === known) this.#known = undefined;
    });
    return known;
  }

  /**
   * One live card at a time (README deviations, item 5), whatever the new request was
   * answered with — a question, a refusal, a freeze: the previous card is kept,
   * collapsed, and can never be signed again, here or on the server.
   */
  #retireLive(): void {
    for (const previous of this.#cards.clear()) this.#retire(previous, "retired");
    this.#editing = undefined;
  }

  #retire(previous: Live, why: "revised" | "retired"): void {
    if (previous.expiry !== undefined) clearTimeout(previous.expiry);
    // DCTA-170: a card being edited in place stops being edited — its sheets close, nothing more is sent.
    if (this.#editor?.live === previous) {
      this.#editor.editor.dispose();
      this.#editor = undefined;
      previous.card.editView(null);
    }
    // A Not sent card is still waiting on the bank's side: it is cancelled there, and stays as it is here.
    if (previous.card.life === "not-sent") void this.#d.api.cancelBundle(previous.bundle.bundle_id, "superseded after a lost signature").catch(() => undefined);
    if (previous.card.life !== "live" && previous.card.life !== "editing") return;
    previous.card.supersede(why);
    void this.#d.api.cancelBundle(previous.bundle.bundle_id, "superseded by a newer card").catch(() => undefined);
  }

  #edit(live: Live): void {
    if (!this.#cards.alive(live)) return;
    // DCTA-170: with an amend source, a conversation's card is edited where it stands (a bank screen's card keeps "Not now").
    const amend = this.#d.amend;
    if (amend !== undefined && live.bundle.signing_payload.origin === undefined) {
      this.#editInPlace(live, amend);
      return;
    }
    if (this.#editing === live) {
      // "Keep it as it was": the same payload is signable again.
      this.#editing = undefined;
      live.card.editing(false);
      return;
    }
    // One card is edited at a time: another card being edited is signable again.
    this.#editing?.card.editing(false);
    this.#editing = live;
    live.card.editing(true);
    this.view.composer.focus();
  }

  #expire(live: Live): void {
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    live.expiry = undefined;
    if (this.#editor?.live === live) {
      this.#editor.editor.dispose();
      this.#editor = undefined;
      live.card.editView(null);
    }
    live.card.expire();
    if (this.#editing === live) this.#editing = undefined;
  }

  /**
   * DCTA-170 (docs/design/v3/03-edit-in-place.md §0.2): the card turns into its edit state where it stands. Each change is
   * the bank's (`amend`); its new bundle is drawn in this card's place on the same entry (`#makeCard`), with its own expiry.
   * Done shows the bank's latest card — "Revised", the version before it kept above (22v, as built), each removal's line
   * and Undo above Sign — and fetches its signing options; a card that was read aloud is read again (spoken.md §3.7).
   */
  #editInPlace(live: Live, amend: AmendSource): void {
    if (this.#editor?.live === live) return;
    if (this.#editor !== undefined) void this.#editor.editor.done();
    this.#editing?.card.editing(false);
    this.#editing = undefined;
    const base = live.card;
    const editor = new CardEditor({
      source: amend,
      card: () => live.card,
      bundle: () => live.bundle,
      swap: (bundle) => this.#swapIn(live, bundle),
      openSheet: (sheet, onDismiss) => this.#editSheet(sheet, onDismiss),
      cancel: (bundle) =>
        this.#d.api.cancelBundle(bundle.bundle_id, "customer removed the last step").then(
          (r) => ({ at: (r as { as_of?: string }).as_of }),
          () => undefined,
        ),
      ended: () => void this.#readEnd(live),
      left: (how) => this.#leftEdit(live, base, how),
      onLimits: this.#d.onLimits,
      // F39: the account sheet lists the customer's own accounts as the bank reads them.
      accounts: () => this.#d.api.accounts().then((r) => r.accounts),
      // H7: a Yes the bank carries stopped the card mid-edit.
      paused: (refused, cancelled) => this.#pausedByEdit(live, refused, cancelled),
    });
    this.#editor = { live, editor };
    editor.start();
  }

  /** The bank's next version of an edited card, drawn in its place on the same entry. */
  #swapIn(live: Live, bundle: BundleView): BankCard {
    const next = this.#makeCard(bundle, live.turn, () => live);
    if (live.card.element.isConnected) live.card.element.replaceWith(next.element);
    live.card = next;
    live.bundle = bundle;
    live.options = undefined;
    live.reg = undefined;
    this.#arm(live);
    return next;
  }

  #leftEdit(live: Live, base: BankCard, how: EditLeft): void {
    if (this.#editor?.live === live) this.#editor = undefined;
    if (how.cancelled) {
      if (live.expiry !== undefined) clearTimeout(live.expiry);
      this.#speakEnd(live);
      return;
    }
    const card = live.card;
    if (how.changed && base !== card) {
      // 22v (as built): the version before the edit stays above, "Superseded", with "Show the previous version".
      base.editView(null);
      base.supersede("revised");
      card.element.before(base.element);
      card.revised();
    }
    // 22s: this edit's removals join an earlier edit's still out (a new version is a new card: its lines are drawn again).
    if (how.changed) this.#listRemovals(live, how.removed);
    card.focus({ kind: "header" });
    if (live.options === undefined && live.reg === undefined && this.#d.amend?.signable !== false) void this.#prepare(live);
    // spoken.md §3.7: a card read aloud before the edit is read again from its new payload (the policy decides, as before).
    if (how.changed) this.#speech.speak({ part: "readback", bundle_id: live.bundle.bundle_id });
  }

  /** A sheet the edit state opens (the amount, the dependants), over the chat. */
  #editSheet(sheet: HTMLElement, onDismiss: () => void): { readonly close: () => Promise<void> } {
    const handle = openSheet(this.#d.host, sheet, {
      behind: [this.view.topbar, this.view.body, this.view.dock],
      reduced: reducedMotion(this.#d.root),
      animate: true,
      onDismiss,
    });
    return { close: () => handle.close() };
  }

  /**
   * 22s after Done: Undo above Sign — the step back from the version it was removed from (`restore`), checked again by the
   * bank; the card stays "Revised", its other removals still listed, and is read again if it was read aloud (spoken.md
   * §3.7: what is read is what is signed). A failure is said in the card's error line (§2.8) by §0.1's table: row 22x with
   * Try again when it never reached the bank; the bank's refusal; the stale line over the latest version; the card's end.
   */
  async #undoAfter(live: Live, stepId: string): Promise<void> {
    const amend = this.#d.amend;
    const entry = live.removed.find((r) => r.stepId === stepId);
    if (amend === undefined || entry === undefined || !this.#cards.alive(live) || live.card.life !== "live" || this.#undoing) return;
    this.#undoing = true;
    live.card.lineError(null);
    const r = await amend.apply(live.bundle, { op: "restore", step_id: stepId, from_bundle_id: entry.from }).finally(() => {
      this.#undoing = false;
    });
    if (!this.#cards.alive(live) || live.card.life !== "live") return;
    if (r.ok) {
      this.#revisedAgain(live, r.bundle);
      live.card.focus({ kind: "header" });
      this.#speech.speak({ part: "readback", bundle_id: live.bundle.bundle_id });
      return;
    }
    if (r.why === "not-awaiting") {
      void this.#readEnd(live);
      return;
    }
    if (r.why === "stale" && r.latest !== undefined) this.#revisedAgain(live, r.latest);
    const text = r.why === "refused" ? refusalText(r.refused) : r.why === "stale" ? EDIT_WORDS.stale : EDIT_WORDS.unreachable;
    live.card.lineError(r.why === "unreachable" ? { text, onRetry: () => void this.#undoAfter(live, stepId) } : { text });
    live.card.focus({ kind: "error" });
  }

  /** After Done, the bank's next version (an Undo's, or the latest after a stale answer): "Revised", the steps still out listed. */
  #revisedAgain(live: Live, bundle: BundleView): void {
    this.#swapIn(live, bundle).revised();
    this.#listRemovals(live);
    if (this.#d.amend?.signable !== false) void this.#prepare(live);
  }

  /**
   * 22s: each step still out of the card's bundle (`add`: this edit's removals), its line and Undo above Sign on the card
   * drawn now. Step ids hold across versions (§0.1's `restore` names one from an older bundle).
   */
  #listRemovals(live: Live, add: readonly Removal[] = []): void {
    const steps = new Set(live.bundle.signing_payload.steps.map((s) => s.step_id));
    const added = new Set(add.map((r) => r.stepId));
    live.removed = [...live.removed.filter((r) => !added.has(r.stepId)), ...add].filter((r) => !steps.has(r.stepId));
    live.card.removedLines(live.removed, (stepId) => void this.#undoAfter(live, stepId));
  }

  /** 409 BUNDLE_NOT_AWAITING (§0.1): the card's real end — signed elsewhere, cancelled or expired — read from the bank. */
  async #readEnd(live: Live): Promise<void> {
    if (this.#editor?.live === live) this.#editor = undefined;
    live.card.editView(null);
    const receipt = await this.#d.api.receipt(live.bundle.bundle_id).catch(() => undefined);
    if (receipt?.signed_at !== undefined) live.card.signed(receipt);
    else if (receipt !== undefined && receipt.state.startsWith("cancelled")) live.card.cancelled();
    else this.#expire(live);
  }

  // ---------------------------------------------------------------- signing
  /**
   * The Sign tap. Its first statement is the ceremony (iOS < 17.4 drops the user
   * gesture across an await): `get()` when the bank knows a passkey, `create()` — and
   * then `get()` by itself — when it does not (§C.11: one tap, two system sheets).
   */
  #sign(live: Live): void {
    if (!this.#cards.alive(live) || this.#signing || !live.card.signable) return;
    if (!webAuthnAvailable()) {
      this.#unsupported();
      return;
    }
    if (live.options !== undefined) this.#signKnown(live, live.options);
    else if (live.reg !== undefined) this.#createAndSign(live, live.reg);
  }

  #signKnown(live: Live, options: AssertionOptions, hints?: readonly string[]): void {
    this.#signing = true;
    const started = Date.now();
    // FIRST asynchronous call of the tap — see app/signing.ts.
    const ceremony = signNow(this.#d.api, live.bundle.bundle_id, options, live.card.answers(), navigator.credentials, hints);
    this.#ceremonyUp(live);
    ceremony.then(
      (receipt) => {
        this.#ceremonyDown();
        markPasskeyHere(this.#d.persona.customerId, localStorage);
        return this.#signed(live, receipt);
      },
      (error: unknown) => {
        this.#ceremonyDown();
        live.card.busy(false);
        if (isCancellation(error)) {
          const timedOut = Date.now() - started >= (options.timeout ?? 60_000) - 1000;
          // The bank knows a passkey and this phone has never held one: the phone had none to
          // offer — recovery, not "sign when you're ready" (§C.11 no-match). Once, not after "Use another device".
          if (!timedOut && hints === undefined && !passkeyHere(this.#d.persona.customerId, localStorage)) this.#recover(live);
          else live.card.failure(timedOut ? "timed-out" : "cancelled");
          return;
        }
        this.#signFailed(live, error);
      },
    );
  }

  /** Not known to the bank: create a passkey and sign with it, in one tap (§C.11). */
  #createAndSign(live: Live, reg: PublicKeyCredentialCreationOptionsJSON, from?: { readonly sheet: SheetHandle; readonly primary: HTMLButtonElement }): void {
    this.#signing = true;
    // FIRST asynchronous call of the tap: createThenSign's first statement is create().
    const outcome = createThenSign(
      {
        api: this.#d.api,
        credentials: navigator.credentials,
        focus: () => whenFocused(document, window, 1000),
        saved: () => {
          this.#known = Promise.resolve(true);
          markPasskeyHere(this.#d.persona.customerId, localStorage);
          // The recovery sheet (if this came from it) leaves; the card carries the second sheet.
          if (from !== undefined) {
            from.sheet.lock(false);
            void this.#closeSheet();
          }
          live.card.line(SAVED_NOW);
        },
        now: () => Date.now(),
      },
      reg,
      live.bundle.bundle_id,
      live.card.answers(),
    );
    this.#ceremonyUp(live);
    outcome.then(
      (r) => {
        this.#ceremonyDown();
        live.card.busy(false);
        switch (r.kind) {
          case "signed":
            live.card.line(null);
            return this.#signed(live, r.receipt);
          case "sign-button":
            // Saved; the phone refused the second sheet. The card's own button, one more tap — never a retry.
            live.reg = undefined;
            live.options = r.options;
            live.card.line(SAVED);
            return undefined;
          case "unsupported":
            if (from !== undefined) from.sheet.lock(false);
            void this.#closeSheet().then(() => this.#unsupported());
            return undefined;
          default:
            // create-cancelled · timed-out: as it was, one notice; fresh options for the next tap.
            // From the recovery sheet: the sheet stays, unlocked, its primary ready again.
            if (from !== undefined) {
              from.sheet.lock(false);
              from.primary.setAttribute("aria-busy", "false");
              return undefined;
            }
            live.card.failure(r.kind);
            void registrationOptions(this.#d.api)
              .then((o) => {
                live.reg = o;
              })
              .catch(() => undefined);
            return undefined;
        }
      },
      (error: unknown) => {
        this.#ceremonyDown();
        live.card.busy(false);
        if (from !== undefined) {
          from.sheet.lock(false);
          from.primary.setAttribute("aria-busy", "false");
        }
        this.#signFailed(live, error);
      },
    );
  }

  /** The platform's prompt is up: busy (label kept), and the chat behind it hidden from assistive tech. */
  #ceremonyUp(live: Live): void {
    live.card.failure(null);
    live.card.busy(true);
    this.view.body.setAttribute("aria-hidden", "true");
  }

  #ceremonyDown(): void {
    this.#signing = false;
    this.view.body.removeAttribute("aria-hidden");
  }

  #signFailed(live: Live, error: unknown): void {
    if (error instanceof ApiError && /EXPIRED/.test(error.reasonCode)) {
      this.#expire(live);
      return;
    }
    if (error instanceof ApiError && (error.reasonCode === OFFLINE || error.status >= 500)) {
      // Until the bank's record answers, the card holds (busy): a second tap would sign blind.
      live.card.busy(true);
      void this.#lostConfirm(live, error).finally(() => {
        if (live.card.life === "live") live.card.busy(false);
      });
      return;
    }
    this.#error(error);
  }

  /**
   * The signature went out and no answer came back. Whether money moved is the bank's
   * record, not a guess (§C.10's Not sent says "Nothing left your accounts"): its read of
   * the bundle decides — still waiting → Not sent; ran → the receipt, as signed; a state
   * mid-flight, or no read at all → the card stays as it was and the chat says the bank
   * can't be reached, claiming nothing.
   */
  async #lostConfirm(live: Live, error: ApiError): Promise<void> {
    const view = await this.#d.api.bundle(live.bundle.bundle_id).catch(() => undefined);
    const next = view === undefined ? "unknown" : afterLostConfirm(view.state, view.expired);
    if (next === "recover") {
      const receipt = await this.#d.api.receipt(live.bundle.bundle_id).catch(() => undefined);
      if (receipt !== undefined) {
        await this.#signed(live, receipt);
        return;
      }
    }
    if (next === "not-sent" || next === "expired" || next === "cancelled") {
      if (live.expiry !== undefined) clearTimeout(live.expiry);
      live.expiry = undefined;
      if (this.#editing === live) this.#editing = undefined;
      if (next === "not-sent") live.card.notSent();
      else if (next === "expired") live.card.expire();
      else live.card.cancelled(this.#d.bankNow?.()?.toISOString());
      if (next === "not-sent") this.#speakEnd(live);
      this.view.scrollToNewest();
      return;
    }
    this.view.message(["Can't reach the bank right now."], { judge: errorCode(error.reasonCode, this.#d.judgeOn?.() === true) });
  }

  /** The last draft's and the last signature's stage timings, as the bank reported them. */
  get timings(): { readonly draft?: Readonly<Record<string, number>>; readonly confirm?: Readonly<Record<string, number>> } {
    return this.#timings;
  }

  async #signed(live: Live, receipt: Receipt): Promise<void> {
    if (receipt.timings !== undefined) this.#timings = { ...this.#timings, confirm: receipt.timings };
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    live.expiry = undefined;
    if (balanceChanged(receipt)) {
      this.#changed(live);
      return;
    }
    live.card.signed(receipt);
    // The end line's time is the bank's `signed_at` (§C.10), which the confirm answer does
    // not carry (only the receipt read does — ask r): read once, never the phone's clock.
    if (receipt.signed_at === undefined) {
      void this.#d.api
        .receipt(live.bundle.bundle_id)
        .then((read) => live.card.signed(read))
        .catch(() => undefined);
    }
    if (receipt.state === "awaiting_step_up") {
      this.#stepUp(live, receipt);
      // DCTA-199 (spoken-cta.md LN3, F52): the code is asked when its field appears — "Enter your code.", by its line, now.
      this.#speech.speak({ part: "reply", line: "token" }, { now: true });
      this.view.scrollToNewest();
      return;
    }
    await this.#succeeded(live, receipt);
  }

  /** 05, the contract's order: signed, and now the token code — on the same card. */
  #stepUp(live: Live, receipt: Receipt): void {
    const flow: StepUpFlow = new StepUpFlow({
      api: this.#d.api,
      card: live.card,
      payload: live.bundle.signing_payload,
      receipt,
      now: () => this.#d.bankNow?.(),
      say: (line) => this.view.assistant(line),
      done: (r) => {
        this.#flows.delete(flow);
        void this.#succeeded(live, r);
      },
      again: () => void this.say(live.turn.said, live.turn.channel),
      failed: (error) => this.#error(error),
    });
    live.stepUp = flow;
    this.#flows.set(flow, live);
  }

  /** What went through: the success sheet from the receipt and the bank's two reads. */
  async #succeeded(live: Live, receipt: Receipt): Promise<void> {
    live.stepUp = undefined;
    if (balanceChanged(receipt)) {
      this.#changed(live);
      return;
    }
    // The final receipt (after a step-up, it is not the one the signature returned): what ran.
    live.card.signed(receipt);
    // DCTA-162 (§3.6): the card's end, read aloud when the card was — instead of whatever is left of its read-back.
    // DCTA-209: a schedule's end is the bank's EN15 ("Scheduled."), by the same reference.
    this.#speakEnd(live);
    // DCTA-209 (F56): a signature that created a schedule — nothing moved: its own sheet, "Scheduled", never "What changed".
    const schedule = live.bundle.signing_payload.schedule;
    if (schedule !== undefined && receipt.steps.some((r) => r.mandate !== undefined)) {
      const onActivity = this.#d.onActivity;
      const sheet = scheduledSheet(scheduledSuccess(live.bundle.signing_payload, live.bundle.firewall?.data_flags ?? []), {
        onDone: () => void this.#closeSheet(),
        ...(onActivity === undefined ? {} : { onSeeIt: () => void this.#closeSheet().then(() => onActivity()) }),
      });
      this.#openSheet(sheet, true);
      return;
    }
    // DCTA-131: the bank moved (or reserved) money — what is behind the chat is read again.
    this.#d.onMoneyMoved?.();
    const [before, after] = await Promise.all([live.before, this.#snapshot()]);
    // The bank's time: its clock now (after a step-up the money ran later than the signature), else the signature's.
    // DCTA-143: the bank's flags on the card's bundle — a flagged string is printed in its inert form on the sheet too.
    const model = successModel(
      live.bundle.signing_payload,
      receipt,
      before,
      after,
      this.#d.bankNow?.()?.toISOString() ?? receipt.signed_at,
      live.bundle.firewall?.data_flags ?? [],
    );
    const onReceipt = this.#d.onReceipt;
    const sheet = successSheet(
      model,
      () => void this.#closeSheet(),
      () => void this.#cancelHeld(live, receipt),
      onReceipt === undefined ? undefined : () => void this.#closeSheet().then(() => onReceipt(live.bundle.bundle_id)),
      // DCTA-198 (P1-B1): a step the identity check stopped when it ran — its "Update my details" does what the refusal's does.
      (action, label) => void this.#closeSheet().then(() => this.#nextAction(action, label, live.turn)),
    );
    this.#openSheet(sheet, true);
    countUp(sheet, reducedMotion(this.#d.root));
  }

  /**
   * DCTA-101 run 2: the bank ran nothing — the balance this card signed moved before the
   * signature arrived (POL-BAL-PROJECTED). No success sheet for money that never moved: the
   * card ends "Not sent" with the reason, the rule and one action, Update.
   */
  #changed(live: Live): void {
    if (this.#editing === live) this.#editing = undefined;
    live.card.balanceChanged();
    this.#speakEnd(live);
    this.view.scrollToNewest();
  }

  /** DCTA-162 (§3.5, §3.6): a card's end — signed, held, paused (after the cancel answered), not sent — named by its bundle. */
  #speakEnd(live: Live): void {
    this.#speech.speak({ part: "end", bundle_id: live.bundle.bundle_id }, { now: true });
  }

  /**
   * Update: the same words go to the bank again and it draws the payment with the balance as it
   * is now (a fresh dry run, a fresh projection, a fresh challenge). No new bubble — the customer
   * said nothing new; the new card is the revision ("Updated. Check it, then sign."). The strict
   * rule stays: nothing is signed or executed here. One request at a time; a tap on a card that is
   * no longer the live one, or once the new card is drawn, does nothing.
   */
  async #update(live: Live): Promise<void> {
    if (!this.#cards.alive(live) || live.card.life !== "changed" || this.#drafting) return;
    if (this.#drafts.path === "fixture" && !this.#drafts.holds(live.turn.said)) {
      this.view.assistant(NOT_YET);
      return;
    }
    this.#drafting = true;
    this.#closeQuestion();
    this.#editing = live;
    try {
      await this.#request(live.turn.said, live.turn.channel);
    } finally {
      if (this.#editing === live) this.#editing = undefined;
      if (this.#cards.newest !== live) live.card.updated();
      this.#drafting = false;
    }
  }

  /**
   * 06: "Cancel the held …" on the sheet. The sheet goes; the held part comes
   * back as its own card — the same signed rows — asking the question in the card.
   */
  async #cancelHeld(live: Live, receipt: Receipt): Promise<void> {
    await this.#closeSheet();
    const heldIds = new Set(receipt.steps.filter((r) => r.state === "held").map((r) => r.step_id ?? r.index));
    const held = live.bundle.signing_payload.steps.filter((s) => heldIds.has(s.step_id) || heldIds.has(s.index));
    if (held.length === 0) return;
    const flow = new HeldFlow({
      api: this.#d.api,
      bundle: live.bundle,
      held,
      now: () => this.#d.bankNow?.(),
      say: (line) => this.view.assistant(line),
      failed: (error) => this.#error(error),
    });
    flow.card.element.classList.add("enter");
    this.view.append(flow.card.element);
    this.view.scrollToNewest();
  }

  // ---------------------------------------------------------------- passkey
  /**
   * Recovery only (§C.11 no-match): the bank has a passkey, this phone offered none.
   * Create one on this phone (the sheet locks while the phone's create sheet is up) ·
   * Use another device (the phone's cross-device prompt) · Not now. Dismissing it is
   * "not now": nothing is signed, the card stays as it was.
   */
  #recover(live: Live): void {
    let reg: PublicKeyCredentialCreationOptionsJSON | undefined;
    const notNow = (): void => void this.#closeSheet();
    const { sheet, primary } = passkeySheet("no-match", {
      primary: {
        label: "Create one on this phone",
        busy: true,
        onSelect: () => {
          if (reg === undefined || this.#signing || !this.#cards.alive(live) || !live.card.signable) return;
          const handle = this.#sheet;
          if (handle === undefined) return;
          // FIRST asynchronous call of the tap: create(). Then the sheet is locked while the phone's sheet is up.
          this.#createAndSign(live, reg, { sheet: handle, primary });
          handle.lock(true);
          primary.setAttribute("aria-busy", "true");
        },
      },
      secondary: {
        label: "Use another device",
        onSelect: () => {
          const options = live.options;
          if (options === undefined || this.#signing || !this.#cards.alive(live) || !live.card.signable) return;
          // FIRST asynchronous call of the tap: get(), asking for another device.
          this.#signKnown(live, options, ["hybrid"]);
          void this.#closeSheet();
        },
      },
      link: { label: "Not now", onSelect: notNow },
    });
    const handle = this.#openSheet(sheet, false, notNow);
    // Registration options for this phone, fetched while the sheet is read (the bank's
    // re-verification before a second passkey is ask k — the mock proceeds).
    registrationOptions(this.#d.api)
      .then((o) => {
        reg = o;
        if (this.#sheet === handle) primary.setAttribute("aria-busy", "false");
      })
      .catch((error: unknown) => {
        if (isUnsupported(error)) void this.#closeSheet().then(() => this.#unsupported());
        else this.#error(error);
      });
  }

  #unsupported(): void {
    this.#openSheet(passkeySheet("unsupported", { primary: { label: "OK", onSelect: () => void this.#closeSheet() } }).sheet, false);
  }

  /**
   * Every sheet dismisses the same way (§C.8): drag, tap above, the grabber, Esc. What that
   * means is the sheet's own "not now" — by default it closes (success, support, not
   * supported); the passkey recovery sheet passes "Not now" (nothing is signed).
   */
  #openSheet(sheet: HTMLElement, animate: boolean, onDismiss: () => void = () => void this.#closeSheet()): SheetHandle {
    const handle = openSheet(this.#d.host, sheet, {
      behind: this.view.parts,
      reduced: reducedMotion(this.#d.root),
      animate,
      onDismiss,
    });
    this.#sheet = handle;
    return handle;
  }

  async #closeSheet(): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    await sheet?.close();
  }

  // ------------------------------------------------------------ the record
  async #snapshot(): Promise<Snapshot | undefined> {
    try {
      const [a, c, p] = await Promise.all([this.#d.api.accounts(), this.#d.api.cards(), this.#d.api.portfolio()]);
      return { accounts: a.accounts, cards: c.cards, holdings: p.holdings };
    } catch {
      return undefined;
    }
  }

  // --------------------------------------------------- outcomes with no card
  /**
   * An outcome with nothing to sign: a question (04), the bank's result lines for a protective action,
   * or a refusal (07). DCTA-124 (Umakhan's ruling, 5 Oct): none of them is a Bank surface — the Bank tag
   * is only the card the customer signs and its result — so each is the assistant's message, in the
   * bank's words verbatim, with its ways on as chips and its rule id for judge mode only.
   */
  async #otherOutcome(
    outcome: DraftOutcome,
    dots: HTMLElement,
    turn: Turn,
    scoped = false,
    read: readonly ReadChip[] = [],
    from: { readonly byWords?: boolean; readonly seen?: readonly Clarification[] } = {},
  ): Promise<void> {
    // DCTA-176 F44: what is drawn is decided once (`outcome-plan.ts`) — the read-aloud names the same objects.
    const plan = outcomePlan(outcome);
    // DCTA-127: small talk ("hi", "thanks", "what can you do?") is answered with the bank's fixed
    // sentence — the assistant's line, nothing else. It is not a new request: a card the customer is
    // about to sign stays live ("thanks" never takes it away), and nothing says "Nothing was sent".
    if (plan.reply !== undefined) {
      this.view.assistant(plan.reply, dots);
      return;
    }
    // A scoped offer answered with no card (a refusal, a question) leaves the card it was offered
    // beside as it is (DCTA-123 (oo)); anything else is a new request, and the live cards retire.
    if (!scoped) this.#retireLive();
    // DCTA-176 (F49): the identity check refused this turn — judge mode's line and Proof remember it.
    if (outcome.eligibility !== undefined) this.#eligible = { said: turn.said, outcome, at: this.#d.bankNow?.()?.toISOString() };
    // DCTA-222: the bank's answer to a how-question asked beside the errand — before the question, the refusal or the result.
    dots = this.#explain(outcome, dots);
    const refused = plan.refusals;
    if (plan.question !== undefined) {
      // DCTA-203: a read the bank ran beside the question — its answer first.
      const reads = bankReadsOf(outcome.executed);
      for (const r of reads) await this.#read(r, r === reads[0] ? dots : this.view.typing());
      if (reads.length > 0) dots = this.view.typing();
      // A step the policy refused is said first; the question about the rest is the newest
      // thing, with its options under it.
      this.#refusals(refused, turn, undefined, false, outcome.eligibility);
      if (refused.length > 0) {
        const moved = this.view.typing();
        dots.remove();
        this.#ask(outcome, moved, turn, { read, ...from });
        return;
      }
      this.#ask(outcome, dots, turn, { read, ...from });
      return;
    }
    const executed = outcome.executed ?? [];
    if (executed.length > 0) {
      // Protective actions ran at once (no signature: tightening only): the bank's own result lines, said by the assistant.
      // DCTA-203 (Claude's rule): a read the bank drafted and ran is the bank's answer — the read it names, made by the app
      // and drawn as its read card (bank-reads.ts), never "can't do that yet". DCTA-176 (§2.1): a payment refused beside
      // them is said after them, once.
      let row: HTMLElement | undefined = dots;
      if (plan.executed.length > 0) {
        this.view.message(plan.executed, { replacing: dots });
        row = undefined;
      }
      for (const r of bankReadsOf(executed)) {
        await this.#read(r, row ?? this.view.typing());
        row = undefined;
      }
      if (refused.length > 0) this.#refusals(refused, turn, row, false, outcome.eligibility);
      else if (row !== undefined) this.view.assistant(NOT_YET, row);
      return;
    }
    if (refused.length === 0) {
      this.view.message(["Nothing was sent."], { replacing: dots });
      return;
    }
    this.#refusals(refused, turn, dots, false, outcome.eligibility);
  }

  /**
   * Refused steps, each as the assistant's message (DCTA-124): the assistant's own limit
   * (`SCOPE_UNSUPPORTED`) with the bank's sentence verbatim — and, for "add a payee", the chip into 17
   * Payees at `add`; a step the bank refused in its words (`refusalMessage`) with its one way on as a
   * chip. `replacing`: the waiting row the first message takes the place of. `beside`: these sit beside
   * a card to sign — an offer on them is scoped to its step (DCTA-123 (oo)).
   */
  #refusals(refused: readonly RefusedStep[], turn: Turn, replacing?: HTMLElement, beside = false, eligibility?: Eligibility): void {
    let row = replacing;
    // DCTA-176 F44: one bank message per refusal — identical refusals are drawn once (B57's rule, defence in depth).
    for (const r of collapseRefusals(refused)) {
      if (pausedByBank(r)) {
        // F27 (b) (backend DCTA-157 B38; Claude's ruling): a Yes the bank carries for this payee — the card's paused end
        // (21f–21h) in the bank's words, its three safe ways on; never a message with one chip, never a signature.
        this.view.pausedStop(bankPausedNotice(r), { onTalk: () => void this.#talk(), onLock: () => this.#lock() }, row);
        row = undefined;
        continue;
      }
      if (assistantSide(r)) {
        if (pointerOf(r) !== undefined) {
          // §F.2 row 5: the bank's sentence, and its next step on the chip's small line — a chip only when the app can
          // open that screen (`wayOn`), and it opens ITS screen (`#nextAction`, DCTA-154).
          const pointer = wayOn(r, this.#openers());
          this.view.assistant(r.customer_message, row);
          row = undefined;
          if (pointer !== undefined) this.view.offer(pointer.label, pointer.small, () => void this.#nextAction({ kind: "bank_surface", surface: pointer.surface }, pointer.label, turn));
          continue;
        }
        // DCTA-136 (F16): the assistant's own limit says the bank's sentence and its next step, verbatim, and draws the
        // bank's offer as a chip used once, by id — §F.2 16a · 16b · 16c ("I can't set the payment to Mum to repeat yet,
        // but it can go once now." · "Ask me again next time." · [Send it once now]) — as a refusal's offer is drawn.
        const m = refusalMessage(r, (a, label) => void this.#nextAction(a, label, turn, beside), this.#openers());
        this.view.message(m.lines, { replacing: row });
        this.view.actions(m.acts);
        row = undefined;
        continue;
      }
      // DCTA-154 (§F.2 21s): every way on the bank sent, in its order — a payment's cap refusal points to the Limits page
      // instead of a figure ("See my limits", then "Talk to us" once B34 lands), each opening ITS screen (#nextAction);
      // used again, and words may answer them ("yes" under two chips is asked about, 16h).
      const openers = this.#openers();
      const toScreen = (r.next_actions ?? []).some((a) => a.action.kind === "bank_surface" && openers[a.action.surface] !== undefined);
      // F49 (§2.5): under the identity check's turn refusal, judge mode reads the bank's eligibility stage, not only the rule.
      const judged = r.scope === "turn" && eligibility !== undefined ? eligibilityLine(eligibility) : undefined;
      this.#say(refusalMessage(r, (a, label) => void this.#nextAction(a, label, turn, beside), openers), row, toScreen, judged);
      row = undefined;
    }
    row?.remove();
  }

  /**
   * DCTA-222 (docs/specs/fe-explained-dcta222.md): the bank's explanations beside this response (`explained[]`) — each one
   * assistant line, the bank's sentence verbatim, said by its read kind as an executed read's line is (`{part: "reply",
   * read}`). No chip, no button. Returns the waiting row what follows in the response is drawn in place of.
   */
  #explain(outcome: DraftOutcome, dots: HTMLElement): HTMLElement {
    let row = dots;
    for (const e of explainedOf(outcome)) {
      this.view.assistant(e.line, row);
      if (e.read !== undefined) this.#speech.speak({ part: "reply", read: e.read });
      row = this.view.typing();
    }
    return row;
  }

  /** The bank's screens this chat can open (DCTA-154): a screen with no opener draws no chip. */
  #openers(): SurfaceOpeners {
    const { onAddPayee, onLimits, onActivity } = this.#d;
    return {
      ...(onAddPayee === undefined ? {} : { add_payee: onAddPayee }),
      ...(onLimits === undefined ? {} : { limits: onLimits }),
      ...(onActivity === undefined ? {} : { activity: onActivity }),
    };
  }

  /** The bank's own result lines (a protective action, run without a signature): said by the assistant. */
  #bankLines(lines: readonly string[]): void {
    this.view.message(lines);
    // DCTA-231 V2: what ran changed the bank (the Paused card's lock; a tightening the customer stopped waiting for).
    this.#d.onBankChanged?.();
  }

  /** A stop the bank made, as the assistant's message: its sentences, its ways on, the rule id in judge mode only. */
  #say(m: Message, replacing?: HTMLElement, words = false, judged?: string): void {
    this.view.message(m.lines, { replacing, judge: this.#d.judgeOn?.() === true ? (judged ?? m.rule) : undefined });
    this.view.actions(m.acts, { words, standing: m.standing === true });
  }

  /**
   * 07 scam-exit: a "Yes" to either question — the safe exit, never a signature. The SAME card turns Paused — the
   * rows stay, nothing is signable here or on the server (the bank cancels the request) — and the safe exits
   * become the actions: Talk to us (the bank's own number, `GET /v1/support`), Lock my money (the protective fast
   * path), Call 1799 · ScamShield. DCTA-155 §F.2 21f–21h (F24): the notice says what the question answered Yes is a
   * sign of (`pausedNotice`), and focus moves to it so it is read before the ways on.
   */
  #paused(live: Live, question: ScamQuestionId): void {
    if (!this.#cards.alive(live)) return;
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    live.expiry = undefined;
    this.#cards.drop(live);
    if (this.#editing === live) this.#editing = undefined;
    // F27 (a) (backend DCTA-157 B38): the cancel carries the card's answers, the Yes among them — the bank carries it for
    // that payee, so the next payment to them is paused too. Never a signature.
    // DCTA-162b (DCTA-161's hand-off ii): the paused end is the bank's answer to this cancel — asked for only once the cancel
    // has answered (before it, the route has nothing to say: 204). A cancel that failed has no end to read.
    const cancelled = this.#d.api.cancelBundle(live.bundle.bundle_id, "customer answered yes at the scam pause", live.card.answers());
    void cancelled.then(
      () => this.#speakEnd(live),
      () => undefined,
    );
    this.#pausedEnd(live, pausedNotice(question));
  }

  /** The card's paused end (§F.2 21f–21h): the pill, the notice, the three safe ways on; nothing signable. */
  #pausedEnd(live: Live, notice: { readonly lead: string; readonly rest: string }): void {
    live.card.phase({
      status: "Paused",
      label: "Bank: paused",
      leaves: false,
      notices: [{ icon: "i-pause", cls: "end", ...notice }],
      focus: "lead",
      acts: [
        { label: "Talk to us", kind: "btn", onSelect: () => void this.#talk() },
        { label: "Lock my money", kind: "ghost", onSelect: () => this.#lock() },
        { label: "Call 1799 · ScamShield", kind: "link", href: "tel:1799", onSelect: () => undefined },
      ],
    });
    this.view.scrollToNewest();
  }

  /**
   * H7 (DCTA-171): an edit refused for a Yes the bank carries — the bank cancelled the cards it names (the edited card among
   * them, when it pays that payee). The edit state ends; each named card shows its paused end in the bank's words.
   */
  #pausedByEdit(live: Live, refused: RefusedStep, cancelled: readonly string[]): void {
    if (this.#editor?.live === live) this.#editor = undefined;
    live.card.editView(null);
    for (const id of cancelled) {
      const named = this.#cards.all().find((l) => l.bundle.bundle_id === id);
      if (named === undefined) continue;
      if (named.expiry !== undefined) clearTimeout(named.expiry);
      named.expiry = undefined;
      this.#cards.drop(named);
      this.#pausedEnd(named, bankPausedNotice(refused));
    }
  }

  /**
   * DCTA-159 ask 1: the cards the bank cancelled with an answer (`cancelled_bundle_ids`). With a new card (a part dropped,
   * a combined card — the coordinator's D1 ruling) each named card is replaced by the card below ("Not sent", its line,
   * no Sign); with a reply (chat "cancel") each ends "Cancelled". The server already refuses to sign them.
   */
  #retireNamed(ids: readonly string[] | undefined, how: "replaced" | "cancelled"): void {
    for (const id of ids ?? []) {
      const live = this.#cards.all().find((l) => l.bundle.bundle_id === id);
      if (live === undefined) continue;
      if (live.expiry !== undefined) clearTimeout(live.expiry);
      live.expiry = undefined;
      if (this.#editor?.live === live) {
        this.#editor.editor.dispose();
        this.#editor = undefined;
        live.card.editView(null);
      }
      if (this.#editing === live) this.#editing = undefined;
      this.#cards.drop(live);
      if (how === "replaced") live.card.supersede("replaced");
      else live.card.cancelled();
    }
  }

  #locking = false;
  /** DCTA-209: what opened a schedule over the chat (`#openSchedule`), focused again on the way back (`returnFocus`). */
  #opener: HTMLElement | undefined;
  #support: SupportContact | undefined;
  #talking = false;

  /**
   * "Talk to us": the bank's number and hours on a sheet, read once from
   * `GET /v1/support` (the operator's config — the one source a scam cannot
   * substitute) and kept for the session. A second tap while it is on its way is
   * swallowed; a read that fails says so in the chat, and nothing changes.
   */
  async #talk(): Promise<void> {
    if (this.#talking || this.#sheet !== undefined) return;
    this.#talking = true;
    try {
      this.#support ??= await this.#d.api.support();
      this.#openSheet(supportSheet(this.#support, () => void this.#closeSheet()), false);
    } catch (error) {
      this.#error(error);
    } finally {
      this.#talking = false;
    }
  }

  /**
   * A refusal's one button (ask u). "Support" is Talk to us; an OFFER is answered by
   * id through the answer route, exactly as a chip is — the figure never travels
   * from here, the bank pins it and runs every rule again, so what comes back is a
   * new card to check and sign (or another refusal). The button's label becomes the
   * customer's own words in the stream. `scoped` (DCTA-124): the offer sat beside a
   * signable card, so the bank answers for that step alone and the first card stays
   * live (DCTA-123 (oo)).
   */
  async #nextAction(action: NextAction, label: string, turn: Turn, scoped = false): Promise<void> {
    if (action.kind === "support") {
      // DCTA-176 F45: "Update my details" opens the refresh (the support sheet is no longer behind it); Talk to us stays.
      const refresh = this.#d.onKycRefresh;
      if (action.topic === "kyc_refresh" && refresh !== undefined && (await refresh())) return;
      await this.#talk();
      return;
    }
    if (action.kind === "bank_surface") {
      // DCTA-154: ITS screen — "See my limits" the Limits page, "Add a payee" 17 Payees at `add` (was: always add-payee).
      openSurface(action, this.#openers());
      return;
    }
    if (action.kind !== "answer" || this.#drafting) return;
    this.#drafting = true;
    try {
      this.#closeQuestion();
      this.view.me(label);
      await this.#run(() => this.#d.api.answer(action.draft_id, { question_id: action.question_id, choice_id: action.choice_id }), turn, { answering: true, scoped });
    } finally {
      this.#drafting = false;
    }
  }

  /** The protective fast path from the Paused card: no draft, no signature (UC-09). */
  #lock(): void {
    if (this.#locking) return;
    this.#locking = true;
    this.#d.api
      .lockFunds()
      .then((r) => this.#bankLines([r.result_summary]))
      .catch((error: unknown) => this.#error(error))
      .finally(() => {
        this.#locking = false;
      });
  }


  /**
   * A request that produced no outcome — each the assistant's message (DCTA-124, Umakhan's ruling). The
   * freeze (07): two readings of the same words disagreed — side by side, nothing to sign. A draft the
   * resolver could not read at all is the assistant's "can't do that one yet"; a question that ended
   * says so in the bank's words; anything else is `#error` — the reason code for judge mode only.
   */
  #failed(error: unknown, turn: Turn, row: HTMLElement, answering = false, question?: OpenQuestion): void {
    // DCTA-136 (F19): the bank did not take the answer and still holds the question — its sentence, then the same
    // question again, its chips under it. The customer's next words answer it; nothing is dropped.
    const kept = question === undefined ? undefined : answerRefused(error);
    if (question !== undefined && kept !== undefined) {
      this.#reopen(question, kept.lead, row, "refused");
      // Its sentence (by the error's audit record), then the question again — the screen's order.
      this.#speakRefusal(error);
      this.#speakQuestion(question);
      return;
    }
    const code = error instanceof ApiError ? error.reasonCode : "";
    if (FREEZE_CODES.has(code) && error instanceof ApiError) {
      // The freeze (§6.7, 11q): the bank's sentence and next step, the two readings side by side —
      // the assistant's message (DCTA-124), with two ways to say it again.
      this.#retireLive();
      // DCTA-188 Change 1: a sentence that names both values (`freeze.rendered: "values"`) is drawn alone.
      const m = freezeMessage(
        { message: error.body["message"], next_steps: error.body["next_steps"], fields: error.body["fields"] as readonly FreezeField[] | undefined, freeze: error.body["freeze"] },
        turn.draft,
        this.#voice.available,
      );
      // DCTA-188 Change 3: judge mode reads the rule, the field and both values, the re-check and both passes under it.
      const judged = this.#d.judgeOn?.() === true;
      const block = judged ? freezeJudgeBlock(error.body) : undefined;
      this.view.message(m.lines, { replacing: row, pairs: m.pairs, judge: errorCode(code, judged), ...(block === undefined ? {} : { judgeBlock: block }) });
      this.view.actions(
        m.acts.map((a) => (a === "say" ? { label: "Say it again", onSelect: () => this.#startListening() } : { label: "Type it", onSelect: () => this.view.composer.focus() })),
      );
      // spoken.md §3.4 (11q): the bank's sentence and next step, named by the freeze's audit record — never the two readings.
      this.#speakRefusal(error);
      return;
    }
    if (code === "DRAFT_SCHEMA_INVALID") {
      // The model path's own sentence asks for one errand at a time; the fixture path has none to give.
      const said = turn.path === "model" && error instanceof ApiError ? error.serverMessage : undefined;
      this.view.assistant(said ?? NOT_YET, row);
      return;
    }
    // A question that ended at the bank (§F.2 11s, F4): the assistant's line, the bank's words.
    const gone = questionGone(error, answering);
    if (gone !== undefined) {
      this.view.assistant(gone, row);
      return;
    }
    // A bank that could not answer (busy, unavailable, no answer): the assistant's line, and the
    // words wait in the field, to send again in a moment. A bank decision: its sentence, said.
    if (this.#error(error, row) === "line") this.#giveBack(turn);
  }

  /**
   * Anything that ended without an outcome, said by the assistant (`failureOf`, §F.2 row 4; DCTA-124):
   * its own line when the bank could not answer, the bank's sentence verbatim when the bank decided —
   * never a card, never a code or a rule id (judge mode sees both). `row`: the waiting row it replaces.
   */
  #error(error: unknown, row?: HTMLElement): "line" | "bank" | "signed-out" {
    if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
      row?.remove();
      this.#d.onSignedOut();
      return "signed-out";
    }
    const failure = failureOf(error);
    const judge = errorCode(failure.code, this.#d.judgeOn?.() === true);
    if (failure.kind === "line") {
      this.view.assistant(failure.text, row, judge);
      return "line";
    }
    // The bank decided (a dry-run refusal, a request it would not take): its sentence, said by the
    // assistant (DCTA-124) — its code and rule id for judge mode only.
    const judged = this.#d.judgeOn?.() === true ? [failure.code, failure.rule].filter((t): t is string => t !== undefined && t !== "").join(" · ") : undefined;
    this.view.message([failure.lead], { replacing: row, judge: judged });
    this.#speakRefusal(error);
    return "bank";
  }

  /** DCTA-188 Change 4: the bank's label on a demo answer (`demo_fault.label`), above the answer it came with. */
  #demoLabel(body: unknown): void {
    const label = demoLabelOf(body);
    if (label !== undefined) this.view.demoLabel(label);
  }

  /** DCTA-188: judge mode only — "Re-checked once: the first reading differed on …; the second agreed", both passes folded. */
  #recheckNote(outcome: DraftOutcome): void {
    if (this.#d.judgeOn?.() !== true) return;
    const recheck = asRecheck(outcome.validator_recheck);
    const line = clearedLine(recheck);
    if (line !== undefined) this.view.judgeNoteNewest(clearedJudgeView(line, passGroups(recheck)));
  }

  /** DCTA-162: a refusal that came as an error has no draft to name — its audit record does (none: nothing is said). */
  #speakRefusal(error: unknown): void {
    const ref = error instanceof ApiError ? error.body["audit_ref"] : undefined;
    if (typeof ref === "string" && ref !== "") this.#speech.speak({ part: "refusal", audit_ref: ref });
  }

  /** DCTA-162: the question asked again (the same object: its draft, its id). */
  #speakQuestion(open: OpenQuestion): void {
    this.#speech.speak({ part: "question", draft_id: open.draftId, ...(open.question.question_id === undefined ? {} : { question_id: open.question.question_id }) });
  }

  // ------------------------------------------------------------ 10 reads
  /**
   * A read-only answer (§6.11): the assistant's one line with no figure in it, and
   * every number on a `.bank.read` card from the bank's own read. Nothing to sign;
   * the next question can follow at once.
   */
  async #read(ask: ReadAsk, dots: HTMLElement = this.view.typing()): Promise<void> {
    const api = this.#d.api;
    // DCTA-162 (spoken.md §1.4): the read the screen shows, for the bank's spoken pointer to it — never a figure. DCTA-204
    // (R4): a read the bank answered is spoken by the kind it names (`speech_read`), none when it names none.
    let shown: string | undefined = ask.kind;
    // DCTA-204 (R3): the bank's own line above its read, verbatim; the app's line only for its own phrasings.
    const said = (fallback: string): string => ask.bank?.line ?? fallback;
    // DCTA-203: the assistant's line, and the card under it only when the read has something to show.
    const answer = (a: ReadAnswer): void => {
      this.view.assistant(said(a.say), dots);
      if (a.rows.length > 0 || (a.kv ?? []).length > 0 || a.stat !== undefined) this.view.append(readCard(a));
    };
    try {
      switch (ask.kind) {
        case "balance": {
          const read = await api.accounts();
          answer(balanceAnswer(read.accounts, read.as_of ?? this.#d.bankNow?.()?.toISOString(), ask.accountId));
          break;
        }
        case "spend": {
          // DCTA-206: the dates or the several categories the bank names (fe-t8-dcta206.md A1, A2), else its period and category.
          answer(spendAnswer(await api.spending(ask.range ?? ask.period), ask.categories ?? ask.category));
          break;
        }
        case "bills": {
          // DCTA-204 (R5): one read — each bill carries its biller's name and digits.
          answer(billsAnswer((await api.bills()).bills));
          shown = "bill_due";
          break;
        }
        case "limits": {
          answer(limitsAnswer(await api.limits(), this.#d.bankNow?.()?.getTime() ?? Date.now()));
          shown = undefined;
          break;
        }
        case "recent": {
          answer(recentAnswer((await api.ledgerEntries(5, ask.accountId)).entries));
          shown = undefined;
          break;
        }
        case "pending": {
          answer(pendingAnswer((await api.pending()).pending));
          shown = undefined;
          break;
        }
        case "scheduled": {
          // DCTA-209 (F61, row 29b): the bank's line, then its read — route 1 exactly as the bank named it (active, the
          // destination it names). Nothing in it: the line alone, no card ("Nothing is scheduled." / "Nothing like that …").
          const { mandates } = await api.mandates({ status: "active", ...(ask.destination === undefined ? {} : { destination_id: ask.destination }) });
          const rows = scheduleRows(mandates).active;
          this.view.assistant(said(rows.length === 0 ? "Nothing is scheduled." : "Here's what's scheduled."), dots);
          if (rows.length > 0) {
            const at = this.#d.bankNow?.()?.toISOString();
            const onActivity = this.#d.onActivity;
            this.view.append(
              scheduledReadCard(
                rows,
                { lead: ask.destination === undefined ? "Scheduled" : `Scheduled to ${rows[0]?.who ?? ""}`, ...(at === undefined ? {} : { asof: `read at ${clock(at)}` }) },
                {
                  onOpen: (id) => this.#openSchedule(id),
                  ...(ask.destination !== undefined || onActivity === undefined ? {} : { onSeeAll: () => onActivity() }),
                },
              ),
            );
          }
          shown = undefined;
          break;
        }
        case "other": {
          // A read the bank answered that this app has no card for: said as the bank's answer — never "can't do that yet" —
          // with its way on (DCTA-204 R7: the audit trail's "See my activity") where the app can open it.
          this.view.assistant(said(BANK_ANSWERED), dots);
          const acts = (ask.ways ?? []).flatMap(({ label, action }) =>
            action.kind === "bank_surface" && this.#openers()[action.surface] !== undefined ? [{ label, onSelect: () => openSurface(action, this.#openers()) }] : [],
          );
          if (acts.length > 0) this.view.actions(acts);
          shown = undefined;
          break;
        }
        case "bill_due": {
          // DCTA-208: the card bill is a credit card's — one with a statement, or the customer's pick when two have one.
          const cards = (await api.cards()).cards;
          const fit = cardBill(ask.cardId === undefined ? cards : cards.filter((c) => c.card_id === ask.cardId));
          if (fit.kind === "many") {
            await this.#whichCard(fit.cards, dots, (card) => ({ kind: "bill_due", cardId: card.card_id }));
            shown = undefined;
            break;
          }
          const a = fit.kind === "one" ? billAnswer(fit.card) : undefined;
          if (a === undefined) {
            this.#notAvailable(dots);
            shown = "not_available";
            break;
          }
          this.view.assistant(a.say, dots);
          this.view.append(readCard(a));
          const pay = a.pay;
          if (pay !== undefined) this.view.offer(pay.label, pay.small, () => void this.say(pay.words));
          break;
        }
        case "card_details": {
          const cards = (await api.cards()).cards;
          let cardId = ask.cardId;
          // DCTA-208 (final QA D3): the app's own words ("Show my card details") reach no bank — they are read as the bank
          // reads "my card": the one card that fits, or its "Which card?" with a chip per card; none held, its own line.
          if (ask.bank === undefined && cardId === undefined && ask.every !== true) {
            const fit = myCard(cards);
            if (fit.kind === "many") {
              await this.#whichCard(fit.cards, dots, (card) => ({ kind: "card_details", cardId: card.card_id }));
              shown = undefined;
              break;
            }
            if (fit.kind === "none") {
              this.view.assistant(NO_CARD_LINE, dots);
              shown = undefined;
              break;
            }
            cardId = fit.card.card_id;
          }
          // DCTA-203: the bank named no card — every card its read lists, never the app's pick (DCTA-202: the one it names).
          const named = cardId === undefined ? undefined : cardAnswer(cards, cardId);
          const shownCards: readonly CardAnswer[] = ask.every === true ? cards.map(cardAnswerFor) : named === undefined ? [] : [named];
          if (shownCards.length === 0) {
            // DCTA-204 (R3): the bank's own empty state ("You don't have a card with us.") when it said one.
            if (ask.bank?.line !== undefined) this.view.assistant(ask.bank.line, dots);
            else this.#notAvailable(dots);
            shown = "not_available";
            break;
          }
          const one = shownCards.length === 1;
          const bankLine = ask.bank?.line;
          const turn = this.view.assistant(bankLine ?? (one ? "Here's your card. Tap Show when you're ready." : SEVERAL_CARDS), dots);
          // The app's own line follows Show and Hide; the bank's stays as the bank said it.
          const line = (text: string): void => {
            const p = turn.querySelector("p");
            if (p !== null && bankLine === undefined) p.textContent = text;
          };
          for (const a of shownCards) {
            const details = new CardDetails(a, {
              reveal: () => api.cardDetails(a.cardId),
              shown: () => line(one ? "Here's your card. Tap Hide when you're done." : SEVERAL_CARDS),
              hidden: () => line(one ? "Here's your card. Tap Show when you're ready." : SEVERAL_CARDS),
            });
            this.#details.add(details);
            this.view.append(details.element);
          }
          break;
        }
        case "not_available":
          this.#notAvailable(dots);
          break;
      }
      this.view.scrollToNewest();
      if (ask.bank !== undefined) shown = ask.bank.speech;
      if (shown !== undefined) this.#speech.speak({ part: "reply", read: shown });
    } catch (error) {
      dots.remove();
      this.#error(error);
    }
  }

  /**
   * DCTA-208: the bank's "Which card?" for the app's own words — a chip per card, in the bank's order and words (the card
   * on the default debit account first). A chip is the customer's pick: their bubble, then `then`'s read of that card; the
   * next request spends the chips (`#closeQuestion`). Not read aloud: the bank holds no fixed line for it yet.
   */
  async #whichCard(cards: readonly CardView[], dots: HTMLElement, then: (card: CardView) => ReadAsk): Promise<void> {
    const api = this.#d.api;
    // Which account is the default debit account only orders the chips: a profile that cannot be read leaves the accounts' order.
    const [{ accounts }, primary] = await Promise.all([api.accounts(), api.customer().then((c) => c.default_debit_account, () => undefined)]);
    const choices = cardChoices(cards, accounts, primary);
    const handle: QuestionHandle = this.view.question(
      WHICH_CARD,
      choices.map((choice, index) => ({ label: choice.label, onSelect: () => void this.#pickCard(handle, index, choice, then) })),
      dots,
    );
    this.#cardPick = handle;
  }

  async #pickCard(handle: QuestionHandle, index: number, choice: CardChoice, then: (card: CardView) => ReadAsk): Promise<void> {
    if (this.#drafting || this.#cardPick !== handle) return;
    this.#cardPick = undefined;
    this.#drafting = true;
    try {
      handle.spend(index);
      this.view.me(choice.label);
      await this.#read(then(choice.card));
    } finally {
      this.#drafting = false;
    }
  }

  /** §8.2: what the bank's reads do not cover — said plainly by the assistant, and Talk to us (DCTA-124: no card). */
  #notAvailable(dots: HTMLElement): void {
    this.view.message(["I can't see that one from here.", "This account can't be read from here yet. Its balance is on your statement, or ask a person."], { replacing: dots });
    this.view.actions([{ label: "Talk to us", onSelect: () => void this.#talk() }]);
  }

  #hideDetails(): void {
    for (const d of this.#details) d.hide();
  }

  #back(): void {
    if (this.#voice.state === "listening") this.#cancelListening();
    this.#hideDetails();
    this.#speech.stop();
    this.#d.onBack();
  }

  dispose(): void {
    this.#speech.stop();
    this.#hideDetails();
    document.removeEventListener("visibilitychange", this.#onHidden);
    for (const live of this.#cards.all()) if (live.expiry !== undefined) clearTimeout(live.expiry);
    for (const flow of this.#flows.keys()) flow.dispose();
    this.#flows.clear();
    // QA-04: a card still being read back after a reload is left as drawn.
    this.#epoch += 1;
    this.#releaseMic();
    if (this.#eosTimer !== undefined) clearTimeout(this.#eosTimer);
    this.#eosTimer = undefined;
    this.#voiceCancelled = true; // a late end (or the watchdog) must not speak into a closed chat
    this.#voice.cancel();
  }
}

/**
 * The bank's stage timings for judge mode (ask g): the model path's own clock first — drafter,
 * validator, the wall between them; attempts are a count, not a time — then the route's stages.
 */
function stageTimings(outcome: DraftOutcome): Readonly<Record<string, number>> {
  const models = Object.fromEntries(Object.entries(outcome.model_timings ?? {}).filter(([k]) => k.endsWith("_ms")));
  return { ...models, ...outcome.timings };
}

/**
 * The assistant's one line over a new card — it hands over, it never explains
 * (copy §8, §8.1). The strongest thing on the card picks the line: two questions,
 * a wait, a token code after the signature, one thing to know, or nothing.
 */
/**
 * DCTA-188 Change 3: judge mode's block under a freeze, from the bank's own blocks — the rule (the freeze's, else the
 * response's first `rule_ids`), the field and both values; the re-check's outcome; both passes. None of it: no block.
 */
export function freezeJudgeBlock(body: Readonly<Record<string, unknown>>): HTMLElement | undefined {
  const named = asFreeze(body["freeze"]);
  const ids = body["rule_ids"];
  const listed = Array.isArray(ids) && typeof ids[0] === "string" ? ids[0] : undefined;
  const freeze = named !== undefined && named.rule_id === undefined && listed !== undefined ? { ...named, rule_id: listed } : named;
  const recheck = asRecheck(body["validator_recheck"]);
  const rows = [freezeRow(freeze), recheckRow(recheck)].filter((r): r is JRow => r !== undefined);
  return rows.length === 0 ? undefined : freezeJudgeView({ rows, passes: passGroups(recheck) });
}

export function readyLine(bundle: BundleView, revising: boolean): string {
  const steps = bundle.signing_payload.steps;
  const n = steps.length;
  if (revising) return n === 1 ? "Updated. Check it, then sign." : "Updated. Check it, then sign once.";
  const levels = (bundle.interventions ?? []).map((i) => i.level);
  if (levels.includes("question")) return "Ready — two questions first.";
  // P2-6: a step was refused — say how many of what was asked are ready (N of M).
  const asked = n + bundle.refused_steps.length;
  if (n > 0 && asked > n) {
    const of = `${(NUMBER[n] ?? String(n)).replace(/^./, (c) => c.toUpperCase())} of the ${(NUMBER[asked] ?? String(asked)).toLowerCase()}`;
    return n === 1 ? `${of} is ready. Check it, then sign.` : `${of} are ready. Check them, then sign once.`;
  }
  if (n === 1) {
    const only = steps[0];
    // P2-7: one line at hold level, whatever the policy's duration.
    if (levels.includes("hold") && only?.policy.hold_until !== undefined) return "Ready — it waits, and here's why.";
    if (only?.policy.outcome === "step_up_required") return "Ready. Sign it, then confirm with your security token.";
    // P1-4 (DCTA-192): "one thing to know" only when an intervention draws words on the card (Mdm Lim's age signal draws none).
    if ((bundle.interventions ?? []).some(drawsClause)) return "Ready. One thing to know, then sign.";
    return "Ready. Check it, then sign.";
  }
  return `${NUMBER[n] ?? String(n)} things ready. Check them, then sign once.`;
}
